// Boston, "Summer sails on the Charles". A warm, clear late-August evening at the blue hour on the Cambridge bank: the
// riverside path on the stone river wall at the east end of the MIT Sailing Pavilion's dock (42.3589 N, 71.0867 W),
// looking east-south-east (about 120 degrees) across the Charles River Basin at Boston, 0.7 to 2.8 km off. The sun has
// just set behind the viewer, so the glass of the Back Bay towers still holds the afterglow while the east goes blue.
// Left to right, as seen from there (bearings from the spot, measured on the map): the Cambridge bank curving away to
// Kendall (50-65), the Charles River Yacht Club's moored sailboats in front of the Longfellow Bridge (67, 160-350 m),
// the Longfellow's Cambridge spans hidden by the bank's trees, its four granite "salt-and-pepper" towers (72, 1.0 km)
// and its Boston end at Charles Circle (80, 1.26 km) with the Red Line crossing on its median; behind it the West End,
// Mass General and the towers by North Station (68-75); Community Boating's dock (85, 1.13 km); Beacon Hill with the
// State House's gold dome (91, 1.88 km) in front of the Financial District (One Congress 83, the Custom House 89, One
// Boston Place 93, Millennium Tower 100, Winthrop Center 101, the Federal Reserve 106, 2.2-2.8 km); the back of the
// Hatch Shell on the Esplanade (99, 1.08 km; its stage opens south, away from Cambridge); the Esplanade's trees and lamps
// along the far bank, Storrow Drive and the Back Bay's brownstones on Beacon Street behind them; then the Back Bay
// skyline: the Berkeley Building and its weather beacon (133, 1.49 km), 200 Clarendon (139, 1.44 km), the towers of
// Copley Place, 111 Huntington's crown (163), the Prudential (165, 1.35 km) and One Dalton (171, 1.48 km).
// What the brief hoped for but this spot can't show: the Harvard Bridge (198) and the Citgo sign (216) stand behind the
// viewer's right shoulder; the MIT pavilion's own dock runs off behind the right shoulder too (200-245), so only its
// last float and the dinghies tied to it come into the frame's right edge. The Zakim Bridge (61) and the Museum of
// Science (54) are behind the Cambridge bank's trees; the Public Garden, Fenway, Faneuil Hall, the North End and MIT's
// Great Dome (behind the viewer) are out of sight. The Hatch Shell's lawn lies behind the shell from here.
// The panorama spans about 120 degrees, so it is compressed between the landmarks (bx below), never reordered: every
// building is drawn at its true height and width for its distance (10 px a degree), the Longfellow at its own scale.
// From here the Prudential's roof (228 m at 1.35 km) stands a little higher than 200 Clarendon's (241 m at 1.44 km),
// and it does here too. The Longfellow and the State House dome are drawn 1.0x and 1.4x (the dome would be 5 px).
// The foreground is the Cambridge side: the stone wall, the riverside path, a strip of lawn, Memorial Drive's old
// sycamores at both edges, riverside lamps and benches, a lobster-roll shack (Pinchy's, invented) with its sign on the
// roof (the city's billboard), the pavilion's last float with dinghies tied up, mallards, a night heron, and people.
// render(t, { crowd }) thins the people (see CROWD); crowd 0 draws nobody, and the shack stands unattended.
//
// The loop's timeline (seconds):
//   4-38      a Red Line train crosses the Longfellow inbound, left to right (outbound 62-96, inbound 124-158,
//             outbound 184-218)
//   12-40     a jet on approach to Logan descends over downtown, right to left (again 92-120, 168-196)
//   20-86     BIG: the day's last Duck Boat comes down the basin from the right, lit, its riders waving; the kid waves
//             back (40-56); it heads for the Longfellow's central arch and passes under it
//   22-34     a cyclist with a lamp rides by, right to left (two more, left to right, at 160-172)
//   46-100    a rowing eight glides downriver, right to left, along the Cambridge side
//   54-64     a jogger runs by, left to right (back at 206-216)
//   98-140    a dog walker comes by, right to left; at 118-126 the dog stops to watch the mallards
//   100-150   BIG: the concert at the Hatch Shell swells: its glow rises over the Esplanade's trees (100-112), holds
//             (112-138) and fades; the couple on the bench turn to listen
//   132-196   a sculler rows by, left to right
//   140-196   a couple strolls by, left to right
//   150-206   BIG: the Prudential's crown turns Red Sox red, then blue, then back to white; she films it
//   176-236   BIG: the last four dinghies sail home in a loose fleet, tack up to the pavilion's float (212-222), drop
//             their sails, are caught by the dockhand and walked along the float out of the frame
//   all loop  thin cloud drifts over the first stars, windows switch on and off, cars run on Storrow Drive, the
//             Esplanade's lamps and the bridge's lamps lay streaks on the water, the Berkeley beacon burns steady blue
//             (the city's weather: clear), the Prudential's aircraft lamps pulse, sailboats beat about the basin, the
//             moored boats swing on their moorings, the dinghies bob, the shack serves a lobster roll every 24 s, the
//             fisher casts every 48 s, the heron strikes every 80 s, the sycamores stir, the cirrus drifts across 200
//             Clarendon's mirror, bats hawk over the water, moths circle the lamps, cars pass on Memorial Drive under the
//             trees on the left, the breeze runs through the lawn's grass and three Canada geese graze on it
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, scroll, hash, rng, bayer, hex,
  makeCanvas, makeHalo, drawHalo, streak, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                            // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const WL = 168;                                     // the far waterline: the Esplanade's edge, everything across stands on it
const QY = 201;                                     // the Cambridge wall's cap: the water ends above this row
const EDGE = 205;                                   // feet of the people at the wall
const LANE_A = 215, LANE_B = 225;                   // feet rows of the people on the path
const GRASS = 233;                                  // where the path gives way to the lawn
const S = 10;                                       // scene px a degree of height (and of a building's own width)
const ACCENT = '#44bb11';                           // the Green Line's green
// Bearing (degrees from north) to column: piecewise linear, compressed between the landmarks, never reordered.
// The Longfellow keeps 9.7 px a degree, near its own scale; the Esplanade between downtown and the Back Bay squeezes.
const KNOTS = [[50, -20], [65, 34], [79.7, 176], [84.6, 186], [89.3, 200], [90.9, 206], [92.7, 213], [99.3, 229],
  [101.1, 235], [105.8, 248], [130, 300], [133.3, 311], [138.6, 332], [160, 372], [162.7, 380], [165.2, 398],
  [171.2, 420], [182, 486]];
function bx(b) {
  if (b <= KNOTS[0][0]) return KNOTS[0][1];
  for (let i = 1; i < KNOTS.length; i++) if (b <= KNOTS[i][0]) {
    const [b0, x0] = KNOTS[i - 1], [b1, x1] = KNOTS[i];
    return x0 + (x1 - x0) * (b - b0) / (b1 - b0);
  }
  return KNOTS[KNOTS.length - 1][1];
}
/** rows above the waterline of something h metres tall at d metres */
const up = (h, d) => S * Math.atan2(h, d) * 180 / Math.PI;
// The heroes: centre column, half width, roof row (true heights from h/d; see the header)
const HAN = { x: 332, x0: 324, x1: 340, top: WL - 95 };          // 200 Clarendon: 240.8 m at 1439 m, 16 px seen
const PRU = { x: 398, x0: 388, x1: 408, top: WL - 96, mast: WL - 116 };   // the Pru: roof 228 m, mast 276 m at 1346 m
const HUN = { x: 379, x0: 369, x1: 389, top: WL - 67 };          // 111 Huntington with its crown: 169 m at 1443 m, 50 m seen (the Pru hides its last px)
const DAL = { x: 420, x0: 414, x1: 426, top: WL - 87 };          // One Dalton: 226 m at 1481 m
const BER = { x: 311, x0: 304, x1: 318, top: WL - 58 };          // the Berkeley Building: 151 m to the beacon at 1488 m
const SH = { x: 206, top: WL - 21 };                              // the State House dome's lantern
const HATCH = { x: 229, w: 16, top: WL - 7 };                     // the Hatch Shell's back
const BB = { x: 254, y: 124, w: 46, h: 20 };                      // Pinchy's sign (the city's billboard)
const SHACK = { x0: 254, x1: 300, cx: 277, roof: 156, eave: 163, base: 214 };
const WIN = { x0: 264, x1: 290, y0: 175, y1: 186 };              // the shack's order window
const LAMPS = [{ x: 150, y: 150 }, { x: 352, y: 150 }];           // the riverside lamps (the lantern's centre)
const BENCH = { x: 200, y: 205 };                                 // a bench at the wall, seen from behind
const DOCK = { x0: 388, y: 193 };                                 // the pavilion's last float, out to the right edge
const SYC_L = { x: 12, base: 258 }, SYC_R = { x: 470, base: 256 }; // Memorial Drive's sycamores at either edge

/* ================= Timeline ================= */
const TRAINS = [{ t0: 4, t1: 38, dir: 1 }, { t0: 62, t1: 96, dir: -1 }, { t0: 124, t1: 158, dir: 1 }, { t0: 184, t1: 218, dir: -1 }];
const JETS_IN = [{ t0: 12, t1: 40 }, { t0: 92, t1: 120 }, { t0: 168, t1: 196 }];
const DUCK = { t0: 20, t1: 86 };
const WAVE = [40, 56];
const EIGHT = { t0: 46, t1: 100 };
const SCULL = { t0: 132, t1: 196 };
const CONCERT = { t0: 100, peak: 112, fall: 138, t1: 150 };
const PRUSHOW = { t0: 150, t1: 206 };
const FLEET = { t0: 176, dock: 212, t1: 236 };
const BIKES = [{ t0: 22, t1: 34, dir: -1, n: 1 }, { t0: 160, t1: 172, dir: 1, n: 2 }];
const JOG = [{ t0: 54, t1: 64, dir: 1 }, { t0: 206, t1: 216, dir: -1 }];
const DOGW = { t0: 98, t1: 140, stop: [118, 126] };
const STROLL = { t0: 140, t1: 196 };
const HERON = [30, 110, 190];                       // the heron's strikes (every 80 s)
// The people: about half of a cast of 18 by default. render(t, { crowd }) shows those whose rank is under crowd; rank 0
// (the couple on the bench, the vendor, the dockhand) stays while anybody does; crowd 0 draws nobody and takes the rod,
// the bikes, the dog, the rowed boats and their crews along. The sailboats, the Duck Boat, the trains and the birds stay.
const CROWD = 0.5;
const here = (rank, crowd) => crowd > 0 && (rank === 0 || rank < crowd);
const RANK = { couple: 0, vendor: 0, sailor: 0, kid: 0.12, fisher: 0.25, queue: 0.3, dog: 0.38, jog: 0.45, friends: 0.55,
  phone: 0.62, bike1: 0.68, stroll: 0.75, bike2: 0.82, scull: 0.88, eight: 0.94, queue2: 0.71 };

/* ================= Pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const MIXED = {};
function mixHex(a, b, t) {
  const k = a + b + t;
  if (MIXED[k]) return MIXED[k];
  const A = C(a), B = C(b);
  return (MIXED[k] = toHex(...A.map((v, i) => Math.round(lerp(v, B[i], clamp(t, 0, 1))))));
}
/** ImageData-backed painter for prerendering */
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    get(i, j) { const o = (j * w + i) * 4; return [d[o], d[o + 1], d[o + 2], d[o + 3]]; },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    line(x0, y0, x1, y1, col) {
      const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
      for (let k = 0; k <= n; k++) this.set(lerp(x0, x1, k / n), lerp(y0, y1, k / n), col);
    },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 (soft < 1 narrows the dithered band) */
function pick(ramp, v, i, j, soft = 1) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * soft) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only); optional period px in x for tileable layers */
function vnoise(x, y, s, px = 0) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const X0 = px ? ((xi % px) + px) % px : xi, X1 = px ? (((xi + 1) % px) + px) % px : xi + 1;
  const a = hash(X0, yi, s), b = hash(X1, yi, s), c = hash(X0, yi + 1, s), d = hash(X1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = pix(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
/** crop a painter to its opaque bounding box (build time) */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const blit = (o, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(o.c, o.x, o.y); ctx.globalAlpha = 1; };
const dot = (x, y, col) => { ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), 1, 1); };
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(Math.round(x), Math.round(y), w, h); };
/** piecewise-linear script: keys [[t, x], ...] sorted by t; the position at t and whether it moves */
function along(keys, t) {
  if (t <= keys[0][0]) return { x: keys[0][1], moving: false };
  for (let i = 1; i < keys.length; i++) if (t < keys[i][0]) {
    const a = keys[i - 1], b = keys[i];
    return { x: lerp(a[1], b[1], (t - a[0]) / (b[0] - a[0])), moving: a[1] !== b[1] };
  }
  return { x: keys[keys.length - 1][1], moving: false };
}
const inWin = (t, a, b) => t >= a && t < b;
/** the red aircraft lamps pulse slowly, all together: 0..1 */
const beacon = t => smooth(0.2, 0.85, 0.5 + 0.5 * wave(t, 80));

/* ================= The sky: the blue hour on the east side, the city's glow under it, thin cloud catching the afterglow ================= */
// The sun set behind the viewer a quarter of an hour ago: the east is a deep, clear blue, paling and warming down to the
// horizon where the city's glow lies over downtown and the Back Bay. No moon tonight; the first stars are out.
const SKY = ['#0a1036', '#0c143d', '#0f1945', '#131f4e', '#172557', '#1c2c61', '#22336a', '#293b73', '#30437b', '#384b82',
  '#425389', '#4d5b8e', '#596292', '#666895', '#736d96', '#807296', '#8c7794', '#987c92'];
const skyV = (x, y) => Math.pow(y / WL, 1.5) * 0.66 + 0.22 * Math.exp(-(((x - 230) / 120) ** 2)) * smooth(70, WL, y)
  + 0.2 * Math.exp(-(((x - 370) / 110) ** 2)) * smooth(60, WL, y) + 0.06 * smooth(120, WL, y);
function renderSky() {
  const p = pix(W, WL);
  for (let y = 0; y < WL; y++) for (let x = 0; x < W; x++) p.set(x, y, pick(SKY, skyV(x, y) + (vnoise(x / 70, y / 10, 3) - 0.5) * 0.03, x, y));
  return p.done();
}
const hazeAt = (x, y) => SKY[Math.min(SKY.length - 1, Math.round(clamp(skyV(x, y), 0, 1) * (SKY.length - 1)))];
const STARS = [];
{
  const r = rng(61);
  while (STARS.length < 44) {
    const x = Math.floor(r() * W), y = Math.floor(r() * 74);
    if (skyV(x, y) > 0.2) continue;
    STARS.push({ x, y, k: 9 + Math.floor(r() * 31), ph: r(), b: 0.3 + r() * r() * 0.7, c: r() < 0.25 ? '#ffe8c8' : r() < 0.5 ? '#cfdcff' : '#eef0ff' });
  }
}
/* thin cirrus, tileable (period W): long faint streaks, their bellies lit rose by the afterglow behind the viewer */
function makeWisps(seed, h, banks, col, colLit, a) {
  const r = rng(seed), streaks = [];
  for (let b = 0; b < banks; b++) {
    const bx0 = r() * W, by = 6 + r() * (h - 12), n = 3 + Math.floor(r() * 4);
    for (let i = 0; i < n; i++) streaks.push({ x: bx0 + (r() - 0.5) * 60, y: by + (r() - 0.5) * 8, rx: 24 + r() * 50, ry: 1.2 + r() * 2.2 });
  }
  const p = pix(W, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = 0, lo = 0;
    for (const s of streaks) {
      let dx = x - s.x; dx -= Math.round(dx / W) * W;
      const q = 1 - (dx / s.rx) ** 2 - ((y - s.y) / s.ry) ** 2;
      if (q > v) { v = q; lo = y > s.y ? 1 : 0; }
    }
    v *= 0.75 + 0.5 * vnoise(x / 9, y / 2, seed, Math.round(W / 9));
    if (v > 0.12 + bayer(x, y) * 0.5) p.set(x, y, lo && v > 0.35 ? colLit : col, a);
  }
  return p.done();
}

/* ================= Windows that switch on and off ================= */
const WINS = [];
function addTog(r, x, y, w, h, col) {
  WINS.push({ x, y, w, h, c: col, k: 2 + Math.floor(r() * 5), ph: r(), duty: 0.3 + r() * 0.45 });
}
const WARMW = ['#ffd890', '#f8c070', '#ffe6b0', '#f0b060'], COOLW = ['#e4ecf8', '#c8d8ec', '#d8e2f0'];

/* ================= Towers ================= */
// A generic tower: x0..x1 (x1 exclusive), its roof row, its ramp (dark to light), windows (floors: lit office floors;
// dots: a condo's windows; grid: a lattice of small lit panes), its crown, and how much of the evening's haze lies on it.
// The face to the left catches the afterglow from the west; the right one is in shade.
const GLASS_B = ['#0d1428', '#141d36', '#1c2844', '#263454', '#324266'];
const GLASS_T = ['#0e1a24', '#152430', '#1d303c', '#283e4a', '#344e5a'];
const STONE = ['#22202a', '#322e38', '#46404a', '#5e565e', '#7a7076'];
const BRICK = ['#1c1216', '#2a1a1c', '#3c2422', '#52302a', '#6a3e32'];
const STEEL = ['#161a24', '#20262f', '#2c333e', '#3c4450', '#4e5864'];
const DARKG = ['#08090d', '#0e1015', '#14161d', '#1c1f27', '#262a33'];
function paintTower(p, r, b) {
  const { x0, x1, top, ramp } = b, base = b.base ?? WL, haze = b.haze ?? 0.3, w = x1 - x0;
  const roofAt = x => {
    const u = (x - x0 + 0.5) / w;
    if (b.crown === 'slope') return top + Math.round((b.dir > 0 ? 1 - u : u) * w * (b.steep ?? 0.6));
    if (b.crown === 'step') return top + (u < 0.25 || u > 0.75 ? 2 : 0);
    if (b.crown === 'pyr') return top + Math.round(Math.abs(u - 0.5) * 2 * (b.pyr ?? 4));
    if (b.crown === 'chamfer') return top + (x === x0 || x === x1 - 1 ? 1 : 0);
    return top;
  };
  for (let x = x0; x < x1; x++) {
    const rt = roofAt(x), face = x - x0 < w * (b.split ?? 0.55) ? 1 : 0;
    for (let y = rt; y < base; y++) {
      let v = 0.36 + (face ? 0.14 : -0.06) + (vnoise(x / 3, y / 4, 11 + x0) - 0.5) * 0.08 + 0.1 * smooth(base - 30, base, y);
      if (x === x0) v += 0.2; else if (x === x1 - 1) v -= 0.16;
      if (y === rt) v += 0.25;
      let col = pick(ramp, v, x, y, 0.6);
      if (haze) col = mixHex(col, hazeAt(x, y), haze);
      p.set(x, y, col);
    }
  }
  const lit = b.lit ?? 0.35, wc = b.cool ? COOLW : WARMW, inner = (x, y) => x > x0 && x < x1 - 1 && y > roofAt(x) + 1;
  const wcol = (col, x, y) => haze > 0.2 ? mixHex(col, hazeAt(x, y), haze * 0.75) : col;
  if (b.win === 'floors') {
    for (let y = top + 3; y < base - 1; y += 2) {
      const v = r();
      if (v < lit) {
        const a = v < lit * 0.55 ? x0 + 1 : x0 + 1 + Math.floor(r() * (w - 3)), e = v < lit * 0.55 ? x1 - 1 : Math.min(x1 - 1, a + 2 + Math.floor(r() * 4));
        const col = wc[Math.floor(r() * wc.length)];
        for (let x = a; x < e; x++) if (inner(x, y)) p.set(x, y, wcol(col, x, y));
      } else if (v < lit + 0.2 && w > 4) addTog(r, x0 + 1 + Math.floor(r() * (w - 4)), y, 2 + Math.floor(r() * 2), 1, wcol(wc[0], x0, y));
    }
  } else if (b.win === 'dots' || b.win === 'grid') {
    const gap = b.win === 'grid' ? 1 : 2;
    for (let y = top + 3; y < base - 1; y += 2) for (let x = x0 + 1; x < x1 - 1; x += gap) {
      const v = r();
      if (!inner(x, y)) continue;
      if (v < lit) p.set(x, y, wcol(wc[Math.floor(r() * wc.length)], x, y));
      else if (v < lit + 0.17) addTog(r, x, y, 1, 1, wcol(wc[Math.floor(r() * wc.length)], x, y));
    }
  }
  if (b.crown === 'lit') for (let x = x0; x < x1; x++) { p.set(x, top, mixHex('#f4f8ff', hazeAt(x, top), haze * 0.5)); p.set(x, top + 1, mixHex('#9ab0d0', hazeAt(x, top), haze)); }
  if (b.crown === 'slope' || b.crown === 'pyr' || b.crown === 'step') for (let x = x0; x < x1; x++) p.set(x, roofAt(x), mixHex(b.edgeCol ?? '#9aaccc', hazeAt(x, top), haze));
  if (b.crown === 'chamfer') for (let x = x0 + 1; x < x1 - 1; x++) p.set(x, top, mixHex('#c8d0dc', hazeAt(x, top), haze));
  if (b.beacon) b.beaconAt = [Math.round((x0 + x1 - 1) / 2), roofAt((x0 + x1) >> 1) - 1];
}

// [bearing, distance m, height m, width m, ramp, windows, lit share, crown, haze]: x and the roof row come from bx/up
const tw = (b, d, h, wm, ramp, win, lit, crown, haze, o = {}) => {
  const x = bx(b), half = Math.max(2, Math.round(up(wm, d) / 2));
  return { x0: Math.round(x - half), x1: Math.round(x + half), top: Math.round(WL - up(h, d)), ramp, win, lit, crown, haze, ...o };
};
// the Financial District behind Beacon Hill, the West End and North Station behind the Longfellow (back, hazy)
const DOWNTOWN = [
  tw(66.5, 2300, 125, 34, GLASS_B, 'dots', 0.3, 'lit', 0.42),            // Avalon North Station
  tw(67.9, 2220, 150, 34, GLASS_T, 'dots', 0.32, 'slope', 0.4, { dir: 1, steep: 0.5 }),   // the Hub on Causeway
  tw(69.2, 2250, 118, 40, STEEL, 'floors', 0.32, 'flat', 0.42, { cool: true }),
  tw(82.8, 2313, 180, 46, GLASS_B, 'floors', 0.3, 'chamfer', 0.36, { cool: true }),   // One Congress
  tw(88.4, 2450, 155, 38, STONE, 'floors', 0.32, 'flat', 0.38),          // 60 State Street
  tw(89.3, 2756, 151, 26, STONE, 'dots', 0.3, 'pyr', 0.36, { pyr: 4, edgeCol: '#b8b0a0', clock: true }),   // the Custom House
  tw(91.4, 2334, 155, 44, STEEL, 'floors', 0.3, 'flat', 0.36, { cool: true }),   // One Beacon
  tw(95.5, 2700, 159, 40, STONE, 'floors', 0.3, 'flat', 0.38),           // One Federal
  tw(96.4, 2750, 180, 42, GLASS_B, 'floors', 0.3, 'step', 0.38, { cool: true }),   // 100 Federal
  tw(92.7, 2273, 183, 40, DARKG, 'grid', 0.18, 'flat', 0.3, { beacon: true }),    // One Boston Place
  tw(100.2, 2550, 155, 40, GLASS_T, 'floors', 0.3, 'flat', 0.38, { cool: true }),  // State Street Financial Center
  tw(104, 2900, 183, 40, GLASS_B, 'floors', 0.28, 'chamfer', 0.42, { cool: true }), // International Place
  tw(101.1, 2493, 211, 50, GLASS_B, 'floors', 0.34, 'lit', 0.32, { cool: true, beacon: true }),   // Winthrop Center
  tw(99.6, 2252, 209, 40, GLASS_T, 'dots', 0.34, 'slope', 0.3, { dir: -1, steep: 0.9, edgeCol: '#c8dcf0', beacon: true }),   // Millennium Tower
  tw(105.8, 2727, 187, 44, STONE, 'floors', 0.3, 'flat', 0.38, { gap: true }),    // the Federal Reserve
];
// Mass General and the West End's towers, nearer, right behind the bridge
const WESTEND = [
  tw(71.4, 1850, 60, 40, BRICK, 'dots', 0.2, 'flat', 0.5), tw(76.2, 1820, 62, 44, BRICK, 'dots', 0.24, 'flat', 0.45),
  tw(73.3, 1541, 64, 60, STEEL, 'grid', 0.22, 'flat', 0.46, { cool: true }),       // MGH: Lunder and Ellison
  tw(74.6, 1600, 74, 50, GLASS_T, 'grid', 0.2, 'flat', 0.46, { cool: true }),
  tw(78.0, 1700, 48, 50, BRICK, 'dots', 0.26, 'flat', 0.42),
];
// the Back Bay's other towers between the heroes, a step dimmer than them
const BACKBAY = [
  tw(128, 1620, 95, 40, STONE, 'floors', 0.32, 'flat', 0.32),            // Liberty Mutual
  tw(121, 1520, 62, 50, BRICK, 'dots', 0.36, 'flat', 0.3),               // the Four Seasons, the Heritage
  tw(135.6, 1460, 100, 36, GLASS_B, 'floors', 0.3, 'flat', 0.3, { cool: true }),   // 500 Boylston
  tw(141, 1700, 125, 34, GLASS_T, 'dots', 0.32, 'lit', 0.3),             // the Clarendon
  tw(145, 1641, 150, 44, STONE, 'dots', 0.34, 'chamfer', 0.28),          // the Copley Marriott
  tw(148, 1620, 122, 44, BRICK, 'dots', 0.34, 'flat', 0.28),             // the Westin Copley Place
  tw(156, 1450, 60, 40, STONE, 'dots', 0.36, 'flat', 0.26),              // the Mandarin Oriental
  tw(159.4, 1480, 80, 36, GLASS_B, 'floors', 0.32, 'flat', 0.28, { cool: true }),  // 888 Boylston
  tw(174, 1250, 95, 30, STONE, 'dots', 0.36, 'flat', 0.24), tw(176.4, 1250, 95, 30, STONE, 'dots', 0.36, 'flat', 0.24),   // the Sheraton's towers
  tw(179.5, 1400, 58, 40, BRICK, 'dots', 0.36, 'flat', 0.26),
];

/* ================= The heroes ================= */
// 200 Clarendon: we face its north-west end, a sheer slab of mirror glass with the deep notch down its middle, and a
// sliver of its long south-west side. The end mirrors the sky behind the viewer, so it holds the afterglow: peach low
// down, rose and violet higher (a vertical mirror shows the sky at the angle you look up at it).
const HAN_GLOW = ['#26285a', '#33336a', '#443e74', '#58487c', '#6e5280', '#865e82', '#9e6a82', '#b47880', '#c6887e', '#d69a80'];
const HAN_SIDE = ['#10162e', '#161d3a', '#1e2546', '#272e54', '#313862'];
function paintHancock(p, r) {
  const { x0, x1, top } = HAN, notch = x0 + 6;
  for (let x = x0; x < x1; x++) for (let y = top; y < WL; y++) {
    const u = (WL - y) / (WL - top), side = x >= x1 - 4;
    let col;
    if (side) col = pick(HAN_SIDE, 0.55 - u * 0.25 + (x === x1 - 1 ? -0.2 : 0) + (vnoise(x, y / 6, 71) - 0.5) * 0.2, x, y, 0.7);
    else {
      let v = 0.9 - u * 0.62 + (vnoise(x / 2, y / 9, 72) - 0.5) * 0.14 + ((x - x0) % 4 === 1 ? 0.04 : 0);
      if (x === x0) v += 0.12;
      if (x === notch) v -= 0.5; else if (x === notch - 1) v += 0.08;
      col = pick(HAN_GLOW, v, x, y, 0.6);
      if (y > top + 3 && (y & 1) && x !== notch && x > x0 && r() < 0.05) col = mixHex(col, '#ffe0b0', 0.35);   // a lit office behind the mirror
    }
    if (y === top) col = side ? '#4a5278' : '#e0b8a8';
    p.set(x, y, col);
  }
  for (let y = top + 1; y < WL; y++) p.set(x0, y, mixHex('#f0c8b0', HAN_GLOW[4], (y - top) / (WL - top) * 0.3));   // the sharp corner catching the light
}
// The Prudential: a box of aluminium mullions and dark glass, its offices lit, the glassy top floors under the roof
// (the observatory, View Boston) glowing white, the mechanical hat, and the antenna mast with its aircraft lamps.
const PRU_STRIP = ['#2a2e3a', '#3a3f4c', '#4e5462', '#666c7a'];
function paintPru(p, r) {
  const { x0, x1, top, mast } = PRU;
  for (let x = x0; x < x1; x++) for (let y = top + 2; y < WL; y++) {
    const strip = (x - x0) % 2 === 0, side = x === x1 - 1;
    let col;
    if (y < top + 9) col = y === top + 2 ? '#c8cede' : (strip ? '#9aa2b4' : '#dfe6f4');   // the top floors' tall glass (the colour show tints them)
    else if (strip) col = pick(PRU_STRIP, 0.62 - (x - x0) * 0.02 + (y === top + 9 ? 0.3 : 0), x, y);
    else {
      const v = r();
      col = v < 0.42 ? WARMW[Math.floor(r() * 4)] : v < 0.52 ? COOLW[Math.floor(r() * 3)] : (y & 1 ? '#141824' : '#1c2030');
      if (v >= 0.52 && v < 0.56) addTog(r, x, y, 1, 1, WARMW[0]);
    }
    if (side) col = mixHex(col, '#0a0e1a', 0.45);
    if (x === x0) col = mixHex(col, '#c8d0e0', 0.35);
    p.set(x, y, mixHex(col, hazeAt(x, y), 0.1));
  }
  for (let x = x0 + 4; x < x1 - 4; x++) { p.set(x, top, '#7a8090'); p.set(x, top + 1, '#4a505e'); }   // the hat
  for (let y = mast; y < top; y++) {                                                        // the mast, wider at its foot
    const k = top - y;
    p.set(PRU.x, y, '#5a5e6a');
    if (k < 9) { p.set(PRU.x - 1, y, (y & 1) ? '#3a3e4a' : '#6a6e7a'); p.set(PRU.x + 1, y, (y & 1) ? '#6a6e7a' : '#3a3e4a'); }
    else if (k < 14) p.set(PRU.x - 1, y, '#4a4e5a');
  }
}
// 111 Huntington: a broad glass shaft, a stepped-in top, and its famous crown: a lit glass dome ringed with fins,
// two thirds of the shaft's width
function paintHuntington(p, r) {
  const { x0, x1, top } = HUN, cx0 = x0 + 3, cx1 = x1 - 3, body = top + 11;
  [[4, 4], [2, 2], [1, 1], [1, 1], [0, 0], [0, 0], [0, 0], [0, 0]].forEach(([a, b], j) => {
    for (let x = cx0 + a; x < cx1 - b; x++) {
      const fin = (x - cx0) % 2 === 0;
      p.set(x, top + j, j === 0 ? '#f4f8ff' : fin ? '#a8c0e0' : j < 4 ? '#eaf2ff' : '#cfe0f6');
    }
  });
  for (let y = top + 8; y < body; y++) for (let x = x0 + 1; x < x1 - 1; x++)             // the stepped-in top under the crown
    p.set(x, y, y === top + 8 ? '#8a9ab8' : mixHex(pick(GLASS_B, 0.45 - (x - x0) * 0.02, x, y, 0.6), hazeAt(x, y), 0.14));
  for (let x = x0; x < x1; x++) for (let y = body; y < WL; y++) {
    let col = pick(GLASS_B, 0.5 - (x - x0) * 0.015 + (x === x0 ? 0.2 : 0) + (y === body ? 0.4 : 0), x, y, 0.6);
    if ((y & 1) && x > x0 && x < x1 - 1 && y > body + 1) { const v = r(); if (v < 0.36) col = COOLW[Math.floor(r() * 3)]; else if (v < 0.42) addTog(r, x, y, 1, 1, COOLW[0]); }
    p.set(x, y, mixHex(col, hazeAt(x, y), 0.14));
  }
}
// One Dalton: a curved triangle of glass, its left face mirroring the afterglow faintly, its crown stepping in
function paintDalton(p, r) {
  const { x0, x1, top } = DAL;
  for (let y = top; y < WL; y++) {
    const inset = y < top + 4 ? 2 : y < top + 8 ? 1 : 0;
    for (let x = x0 + inset; x < x1 - inset; x++) {
      const left = x - x0 < 8, u = (WL - y) / (WL - top);
      let col = left ? pick(HAN_GLOW, 0.55 - u * 0.45 + ((y - top) % 3 === 0 ? 0.08 : 0) - (x - x0) * 0.02, x, y, 0.6)
        : pick(HAN_SIDE, 0.6 - (x - x0 - 8) * 0.12, x, y, 0.6);
      if (y > top + 6 && (y & 1) && x > x0 + inset && x < x1 - inset - 1) { const v = r(); if (v < 0.24) col = mixHex(WARMW[Math.floor(r() * 4)], col, 0.25); else if (v < 0.28) addTog(r, x, y, 1, 1, WARMW[1]); }
      if (x === x0 + inset) col = mixHex(col, '#e8c8c0', 0.3);
      p.set(x, y, mixHex(col, hazeAt(x, y), 0.14));
    }
    if (y === top || y === top + 4 || y === top + 8) for (let x = x0 + inset; x < x1 - inset; x++) p.set(x, y, '#b8a8c0');
  }
}
// The Berkeley Building (the old John Hancock): limestone, stepping back to a hipped roof and the weather beacon's spire
function paintBerkeley(p, r) {
  const { x0, top } = BER;
  const tiers = [[0, 14, WL - 28, WL], [2, 10, WL - 42, WL - 28], [3, 8, WL - 48, WL - 42]];
  for (const [dx, w, y0, y1] of tiers) for (let x = x0 + dx; x < x0 + dx + w; x++) for (let y = y0; y < y1; y++) {
    const u = (x - x0 - dx) / (w - 1);
    let col = pick(STONE, 0.6 - u * 0.4 + (y === y0 ? 0.3 : 0) + (x === x0 + dx ? 0.15 : 0), x, y, 0.6);
    if (y > y0 + 1 && (y & 1) && x > x0 + dx && x < x0 + dx + w - 1) { const v = r(); if (v < 0.36) col = WARMW[Math.floor(r() * 4)]; else if (v < 0.42) addTog(r, x, y, 1, 1, WARMW[2]); }
    p.set(x, y, mixHex(col, hazeAt(x, y), 0.2));
  }
  [[4, 6], [5, 4], [6, 2]].forEach(([dx, w], j) => { for (let x = x0 + dx; x < x0 + dx + w; x++) p.set(x, WL - 49 - j, x === x0 + dx ? '#7a7680' : '#4a4652'); });   // the hipped roof
  for (let y = top + 3; y < WL - 51; y++) { p.set(BER.x - 1, y, '#3a3844'); p.set(BER.x, y, '#2a2834'); }   // the beacon's spire (lit per frame)
}

/** the bearing that maps to column x (the inverse of bx) */
function bxInv(x) {
  if (x <= KNOTS[0][1]) return KNOTS[0][0];
  for (let i = 1; i < KNOTS.length; i++) if (x <= KNOTS[i][1]) {
    const [b0, x0] = KNOTS[i - 1], [b1, x1] = KNOTS[i];
    return b0 + (b1 - b0) * (x - x0) / (x1 - x0);
  }
  return KNOTS[KNOTS.length - 1][0];
}
/** the far bank's distance along a bearing (m), read off the map: Charles Street to the Back Bay straight across */
const BANK_D = [[78, 1260], [86, 1150], [100, 1090], [115, 980], [130, 860], [145, 760], [160, 690], [182, 650]];
function bankD(b) {
  if (b <= BANK_D[0][0]) return BANK_D[0][1];
  for (let i = 1; i < BANK_D.length; i++) if (b <= BANK_D[i][0]) {
    const [b0, d0] = BANK_D[i - 1], [b1, d1] = BANK_D[i];
    return lerp(d0, d1, (b - b0) / (b1 - b0));
  }
  return BANK_D[BANK_D.length - 1][1];
}

/* ================= Beacon Hill and the State House ================= */
// Brick rowhouses climbing from Charles Street to the hill's top, the State House on it: its brick front and white
// portico, floodlit, and the gold dome with its lantern and pinecone (drawn 1.4x: true to scale it would be 5 px).
const GOLD = ['#5a3a0c', '#8a5c14', '#b8841e', '#e0aa30', '#f6cc58', '#fff0a8'];
const HILL = x => (x < 178 ? 0 : x <= SH.x ? Math.round(6 * Math.sin(Math.PI / 2 * (x - 178) / (SH.x - 178))) : Math.max(0, Math.round(6 - (x - SH.x) * 0.4)));
function paintHill(p, r) {
  for (let x = 176; x < 226; x++) {
    const g = HILL(x), roof = WL - g - 4 - (hash(x >> 2, 5, 31) < 0.5 ? 1 : 0) - (x > 186 && x < 200 ? 1 : 0);
    for (let y = roof; y < WL; y++) {
      let col = pick(BRICK, 0.4 + (y === roof ? 0.3 : 0) + ((x >> 2) & 1 ? 0.08 : -0.04), x, y, 0.6);
      if (y > roof && (y - roof) % 2 === 1 && (x % 3) === 1 && r() < 0.45) col = WARMW[Math.floor(r() * 4)];
      p.set(x, y, mixHex(col, hazeAt(x, y), 0.18));
    }
  }
  const fy = WL - HILL(SH.x) - 4;                                                           // the State House's front, on the hilltop
  for (let x = SH.x - 6; x <= SH.x + 6; x++) for (let y = fy - 7; y < fy + 1; y++) {
    let col = pick(['#5a3028', '#7a4434', '#9a5a40', '#c08060'], 0.6 - Math.abs(x - SH.x) * 0.04 + (y === fy - 7 ? 0.3 : 0), x, y);
    if (Math.abs(x - SH.x) <= 2 && y > fy - 5) col = (x - SH.x) % 2 === 0 ? '#f4ecdc' : '#a89c88';   // the portico's columns, floodlit
    if (y === fy - 5 && Math.abs(x - SH.x) <= 3) col = '#fff4e0';                           // the pediment
    if (Math.abs(x - SH.x) > 3 && (y - fy) % 2 === 0 && x % 2 === 0) col = '#ffe2a8';       // the wings' windows
    p.set(x, y, col);
  }
  const dy = fy - 8;                                                                         // the drum, the dome, the lantern, the pinecone
  for (let x = SH.x - 4; x <= SH.x + 4; x++) p.set(x, dy, x === SH.x - 4 ? '#e8e0d0' : '#b8b0a0');
  [[3, 3], [3, 3], [2, 2], [1, 1]].forEach(([a, b], j) => {
    for (let x = SH.x - a; x <= SH.x + b; x++) p.set(x, dy - 1 - j, pick(GOLD, 0.92 - (x - SH.x + a) * 0.1 - j * 0.04 + (x === SH.x - a ? 0.1 : 0), x, dy - 1 - j));
  });
  p.set(SH.x, dy - 5, '#f0e8d8'); p.set(SH.x, dy - 6, '#d8d0c0'); p.set(SH.x - 1, dy - 5, '#c8c0b0'); p.set(SH.x, dy - 7, GOLD[4]);
}

/* ================= The far bank: Charles Circle, Community Boating, the Esplanade, Storrow Drive, Beacon Street ================= */
const TREE = ['#060a0c', '#0a1012', '#0e1618', '#141e1e', '#1c2824'];
const ESP_LAMPS = [], CB_MASTS = [], STORROW_GAP = new Uint8Array((W >> 2) + 2);
function paintBank(p, r) {
  // the brownstones of Beacon Street, behind Storrow Drive (taller to the right, where the bank is nearer)
  let x = 222;
  while (x < W) {
    const b = bxInv(x), d = bankD(b) + 120, w = 3 + Math.floor(r() * 4), h = 17 + r() * 6, top = Math.round(WL - up(h, d));
    for (let i = x; i < Math.min(W, x + w); i++) for (let y = top; y < WL; y++) {
      let col = pick(r() < 0.5 ? BRICK : STONE, 0.42 + (i === x ? 0.2 : 0) + (y === top ? 0.3 : 0), i, y, 0.6);
      if (y > top + 1 && (y - top) % 2 === 0 && i > x && r() < 0.32) col = WARMW[Math.floor(r() * 4)];
      else if (y > top + 1 && (y - top) % 2 === 0 && i > x && r() < 0.1) addTog(r, i, y, 1, 1, WARMW[1]);
      p.set(i, y, mixHex(col, hazeAt(i, y), 0.2));
    }
    x += w;
  }
  // the Esplanade's trees along the water (lindens, oaks and willows), lamps on the river path between them
  for (let x0 = 176; x0 < W + 6; x0 += 3 + Math.floor(r() * 4)) {
    const b = bxInv(x0), d = bankD(b) + 25, rad = Math.max(2, Math.round(up(4 + r() * 3, d))), cy = WL - 2 - rad;
    if (x0 > HATCH.x - HATCH.w / 2 - 2 && x0 < HATCH.x + HATCH.w / 2 + 2 && r() < 0.7) continue;   // the shell's clearing
    for (let y = cy - rad; y <= WL - 1; y++) for (let i = x0 - rad - 1; i <= x0 + rad + 1; i++) {
      const dd = Math.hypot((i - x0) / (rad + 1), (y - cy) / rad);
      if (y < WL - 2 && dd > 1 + (bayer(i, y) - 0.5) * 0.5) continue;
      if (y >= WL - 2 && Math.abs(i - x0) > 0) continue;                                       // the trunk under the crown
      p.set(i, y, pick(TREE, 0.3 + (y - cy) / rad * 0.25 + (vnoise(i / 2, y / 2, 33) - 0.5) * 0.4, i, y));
    }
  }
  for (let x0 = 182; x0 < W; x0 += 9 + Math.floor(r() * 5)) ESP_LAMPS.push(x0);
  for (let x0 = 176; x0 < W; x0 += 4) STORROW_GAP[x0 >> 2] = hash(x0 >> 2, 9, 41) > 0.3 ? 1 : 0;
  for (let x0 = 176; x0 < W; x0++) { p.set(x0, WL - 1, pick(['#1a1c24', '#2a2a32', '#3a3a40'], 0.4 + 0.3 * Math.sin(x0 * 0.9), x0, WL - 1)); p.set(x0, WL, '#0c0e16'); }   // the riprap edge
  // Charles Circle at the bridge's foot: the station's glass head house, the old jail's granite (the Liberty Hotel)
  for (let x0 = 170; x0 < 182; x0++) for (let y = WL - 8; y < WL; y++) {
    let col = x0 < 176 ? (y === WL - 8 ? '#9aa8b8' : (y - WL) % 3 === 0 ? '#2a3440' : '#a8c0d8') : pick(STONE, 0.5 + (y === WL - 8 ? 0.3 : 0), x0, y);
    if (x0 >= 176 && (y & 1) && (x0 & 1)) col = '#ffd890';
    p.set(x0, y, col);
  }
  for (let x0 = 156; x0 < 168; x0++) for (let y = WL - 12; y < WL - 2; y++) {             // the Liberty Hotel's rotunda behind the bridge's end
    let col = pick(STONE, 0.55 - Math.abs(x0 - 162) * 0.04 + (y === WL - 12 ? 0.3 : 0), x0, y);
    if ((y - WL) % 3 === 0 && (x0 % 2 === 0)) col = '#ffe2a8';
    p.set(x0, y, mixHex(col, hazeAt(x0, y), 0.16));
  }
  // Community Boating: the boathouse, its dock and a forest of little masts, keelboats on their moorings
  for (let x0 = 182; x0 < 192; x0++) for (let y = WL - 4; y < WL; y++) p.set(x0, y, y === WL - 4 ? '#6a5048' : (x0 % 3 === 0 && y === WL - 2) ? '#ffe0a0' : '#3a2826');
  for (const mx of [183, 185, 186, 188, 190, 191, 193, 195]) { const h = 3 + Math.floor(r() * 3); for (let y = WL - h; y < WL; y++) p.set(mx, y, '#8a8c98'); CB_MASTS.push(mx); }
  // the Hatch Shell, from behind: a pale terrazzo quarter-dome rising out of its clearing
  const { x: hx, w: hw, top: ht } = HATCH;
  for (let i = -hw / 2; i <= hw / 2; i++) {
    const u = i / (hw / 2), yt = Math.round(WL - 1 - (WL - 1 - ht) * Math.sqrt(Math.max(0, 1 - u * u)));
    for (let y = yt; y < WL - 1; y++) p.set(hx + i, y, pick(['#3a3a48', '#50505c', '#68666e', '#848086', '#a49e9e'], 0.6 - u * 0.3 - (y - yt) * 0.07 + (y === yt ? 0.2 : 0), hx + i, y));
  }
}

/* ================= The Cambridge bank, receding to the left towards Kendall ================= */
// Seen along its own line: the sycamores of Memorial Drive and MIT's buildings behind them close the frame's left side,
// hiding the Longfellow's first spans; the bank's lamps run off towards the bridge.
const CAMB_LAMPS = [[30, 172, 3], [24, 175, 4], [15, 180, 6], [2, 187, 8]];
function paintCambridge(p, r) {
  for (const [x0, x1, top, ramp] of [[-4, 10, 104, STONE], [8, 22, 118, GLASS_T], [20, 32, 132, BRICK], [28, 37, 146, STONE]]) {
    for (let x = Math.max(0, x0); x < x1; x++) for (let y = top; y < WL + 6; y++) {
      let col = pick(ramp, 0.45 + (x === x0 ? 0.2 : 0) + (y === top ? 0.3 : 0) - (x - x0) * 0.02, x, y, 0.6);
      if (y > top + 1 && (y & 1) && r() < 0.42) col = COOLW[Math.floor(r() * 3)];
      p.set(x, y, mixHex(col, hazeAt(x, y), 0.16));
    }
  }
  const edge = x => Math.round(lerp(196, 169, x / 37));                                     // the bank's edge (the water to its right)
  for (let x = 0; x < 40; x++) {
    const crown = Math.round(lerp(96, 162, Math.pow(x / 39, 0.8)) + (vnoise(x / 3, 1, 37) - 0.5) * 10);
    for (let y = crown; y < H; y++) {
      if (y > edge(x) && x > 6) break;
      const under = y > edge(x) - 6;
      p.set(x, y, under ? pick(['#0a0c0e', '#121618', '#1a2020'], 0.4 + (vnoise(x, y, 38) - 0.5), x, y) : pick(TREE, 0.25 + (vnoise(x / 2.5, y / 2.5, 39) - 0.5) * 0.6 + (y - crown) * 0.01, x, y, 0.8));
    }
    if (x > 6) p.set(x, edge(x), '#3a3c44');                                                  // the wall's lit edge
  }
}

/* ================= The Longfellow Bridge ================= */
// Projected from the map: its 11 steel arches on granite piers (spans 101.5 to 188.5 ft), the deck at 8.5 m, and on the
// two central piers the four towers, 58 ft tall (17.7 m), two each side. Local metres: e east, n north of the viewer.
const LFM = { m: [944, 331], u: [0.941, -0.337], p: [0.337, 0.941], half: 16 };   // midpoint, axis (Cambridge to Boston), downstream normal
const LF_SPANS = [101.5, 112, 123.5, 145, 165, 188.5, 165, 145, 123.5, 112, 101.5].map(f => f * 0.3048);
const LF_PIERS = [];                                // [s0, s1] of every pier along the axis
let LF_LEN = 0;
{
  let s = 0;
  LF_SPANS.forEach((len, i) => { s += len; if (i < LF_SPANS.length - 1) { const w = i === 4 || i === 5 ? 16 : 4; LF_PIERS.push([s, s + w]); s += w; } });
  LF_LEN = s;
}
/** screen point of a spot on the bridge: s along the axis from Cambridge, side -1 the near (upstream) face, 0 the median,
    1 the far face; h metres above the water */
function lfAt(s, side, h) {
  const k = s - LF_LEN / 2, e = LFM.m[0] + LFM.u[0] * k + LFM.p[0] * side * LFM.half, n = LFM.m[1] + LFM.u[1] * k + LFM.p[1] * side * LFM.half;
  const d = Math.hypot(e, n);
  return [bx(Math.atan2(e, n) * 180 / Math.PI), WL - up(h, d), d];
}
const LF_TOWERS = [];                               // [s, side] of each tower
LF_PIERS.forEach(([a, b], i) => { if (i === 4 || i === 5) for (const side of [1, -1]) LF_TOWERS.push([(a + b) / 2, side]); });
const PARA_Y = new Int16Array(W).fill(0);           // the near parapet's row per column (the train runs just behind it)
const LF_LAMPS = [];                                // the bridge's lamp posts on the near parapet: [x, y]
const GRANITE = ['#2a2a32', '#3c3a42', '#545058', '#6e686e', '#8a8288', '#a89ea0'];
const LF_TOWER = ['#3a3034', '#5a4a4a', '#7c6a64', '#9e8a7e', '#c0aa98', '#e0ccb4'];
function archH(s) {                                 // the arch's underside along a span (m), or -1 on a pier
  let a = 0;
  for (let i = 0; i < LF_SPANS.length; i++) {
    const len = LF_SPANS[i];
    if (s < a + len) { const t = (s - a) / len; return 1.4 + 5.6 * Math.pow(Math.sin(Math.PI * t), 0.6); }
    a += len;
    if (i < LF_PIERS.length) { const [p0, p1] = LF_PIERS[i]; if (s < p1) return -1; a = p1; }
  }
  return -1;
}
function paintTowerLF(p, s, side) {
  const [x, , d] = lfAt(s, side, 0), pxm = (S * 180 / Math.PI) / d;                          // px a metre across, here
  const yB = Math.round(WL - up(0, d)), yT = Math.round(WL - up(19, d)), yC = Math.round(WL - up(15.8, d)), yBand = Math.round(WL - up(12.4, d));
  for (let k = -3; k <= 3; k += 0.25) {
    const xs = Math.round(x + k * pxm), shade = 0.92 - (k + 3) / 6 * 0.62;                    // lit from the left (the bridge's lamps, the west)
    for (let y = yT; y <= yB; y++) {
      if (y < yC && Math.abs(k) > 3 * (y - yT + 0.6) / (yC - yT + 1)) continue;              // the shaker's conical cap
      let v = shade + (y === yC ? 0.2 : 0) + (y === yBand ? 0.18 : 0) + (y === yBand + 1 ? -0.2 : 0) - (y > yB - 3 ? 0.25 : 0) - (y < yC ? 0.12 : 0);
      if (side > 0) v -= 0.22;
      p.set(xs, y, pick(LF_TOWER, v, xs, y, 0.5));
    }
  }
}
function renderLongfellow() {
  const back = pix(W, WL + 2), front = pix(W, WL + 2);
  for (const [s, side] of LF_TOWERS) if (side > 0) paintTowerLF(back, s, side);              // the far towers, behind the deck
  for (let s = 0; s <= LF_LEN; s += 0.25) {
    const [x] = lfAt(s, -1, 9), [, yPara] = lfAt(s, -1, 9.8), [, yFas] = lfAt(s, -1, 7.6), xi = Math.round(x);
    if (xi < 0 || xi >= W) continue;
    const ah = archH(s), [, yArch] = lfAt(s, -1, Math.max(0, ah));
    PARA_Y[xi] = Math.round(yPara);
    back.set(xi, Math.round(yPara) - 2, '#2a2c38');                                          // the far parapet, over the median
    for (let y = Math.round(yPara); y <= WL; y++) {
      let col = null;
      if (y <= Math.round(yPara)) col = '#a89a90';                                          // the parapet, lit by the bridge's lamps
      else if (y <= Math.round(yFas)) col = y === Math.round(yFas) ? '#121218' : '#3a3a44';   // the deck's fascia
      else if (ah < 0) col = pick(GRANITE, 0.62 - (y - yFas) * 0.04, xi, y);                // a pier
      else if (y <= Math.round(yArch)) col = y === Math.round(yArch) ? '#6a6a74' : '#1a1a22'; // the spandrel over the arch rib
      if (col) front.set(xi, y, col);
    }
  }
  for (const [s0, s1] of LF_PIERS) for (const s of [s0 + 1, s1 - 1]) { const [x, y] = lfAt(s, -1, 10.6); LF_LAMPS.push([Math.round(x), Math.round(y)]); }
  for (const [s, side] of LF_TOWERS) if (side < 0) paintTowerLF(front, s, side);             // the near towers, in front of the train
  return { back: trim(back), front: trim(front) };
}

/* ================= The basin: blue-hour water, the city's mirror broken by ripples, the lights' long streaks ================= */
function renderWater() {
  const h = QY - WL, p = pix(W, h), P = ['#070b22', '#0a1029', '#0e1532', '#131b3c', '#1a2246'];
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++)
    p.set(x, y, pick(P, 0.7 - 0.45 * (y / h) + (vnoise(x / 40, y / 3, 9) - 0.5) * 0.2, x, y, 0.5));
  return p.done();
}
/** the far side as the water mirrors it: a still of what stands over the waterline, upside down, softened and dimmed */
function renderMirror(src) {
  const sx = src.getContext('2d'), data = sx.getImageData(0, 0, W, WL).data, rows = QY - WL, p = pix(W, rows);
  for (let k = 0; k < rows; k++) for (let i = 0; i < W; i++) {
    const sy = WL - 1 - k * 1.6, acc = [0, 0, 0];
    let n = 0;
    for (let dy = -2; dy <= 2; dy++) for (let dx = -1; dx <= 1; dx++) {
      const yy = Math.round(sy + dy), xx = clamp(i + dx, 0, W - 1);
      if (yy < 0 || yy >= WL) continue;
      const o = (yy * W + xx) * 4;
      acc[0] += data[o]; acc[1] += data[o + 1]; acc[2] += data[o + 2]; n++;
    }
    const f = 0.7 - k * 0.008;
    p.set(i, k, [0, 1, 2].map(c => Math.min(255, Math.floor((acc[c] / n * f + [8, 12, 40][c] * (1 - f)) / 10 + bayer(i, k)) * 10)));
  }
  return p.done();
}
const GLINTS = [];                                  // ripples catching the light, drifting
{
  const r = rng(909);
  for (let i = 0; i < 130; i++) GLINTS.push({ y: WL + 2 + Math.floor(r() * (QY - WL - 3)), x: r(), k: (r() < 0.5 ? -1 : 1) * (1 + Math.floor(r() * 4)),
    len: 2 + Math.floor(r() * 6), kf: 18 + Math.floor(r() * 40), ph: r(), c: r() < 0.25 ? '#c8a070' : r() < 0.7 ? '#58608c' : '#04061a' });
}
// the brightest lights lay long, broken streaks across the water (the Pru's crown follows its colour, per frame)
const STREAKS = [
  { x: HAN.x - 3, w: 7, col: '#d89a86', a: 0.2, seed: 2 }, { x: HUN.x, w: 6, col: '#d8e6ff', a: 0.16, seed: 3 },
  { x: DAL.x - 2, w: 5, col: '#c8a0a8', a: 0.12, seed: 4 }, { x: SH.x, w: 4, col: '#f6c850', a: 0.22, seed: 5 },
  { x: 232, w: 8, col: '#e0e8f8', a: 0.1, seed: 6 }, { x: 116, w: 8, col: '#e8f0ff', a: 0.1, seed: 7 },
  { x: 172, w: 5, col: '#ffd890', a: 0.14, seed: 8 }, { x: 62, w: 6, col: '#c8d8f0', a: 0.08, seed: 9 },
];

/* ================= Boats ================= */
// The Charles River Yacht Club's sailboats on their moorings off the Cambridge bank, between us and the bridge: hulls
// white or navy, masts with their anchor lights. They swing a little on the breeze. [x, waterline, hull length, mast]
const MOORED = [[44, 176, 18, 30, '#e8e8e4'], [58, 174, 14, 24, '#2a3a5a'], [77, 173, 13, 22, '#e0ded8'], [90, 175, 15, 26, '#e8e8e4'], [122, 172, 11, 19, '#8a2a2a']];
function drawMoored(t) {
  MOORED.forEach(([x, y, len, mh, col], i) => {
    const bob = Math.round(0.6 * wave(t, 24 + i * 3, i * 0.17)), sway = wave(t, 6 + i, i * 0.3), yy = y + bob, half = len >> 1;
    ctx.globalAlpha = 0.25; R(x - half, yy + 1, len, 1, '#8890b8'); ctx.globalAlpha = 1;   // its mirror
    R(x - half + 1, yy - 2, len - 2, 2, col); R(x - half + 2, yy, len - 4, 1, mixHex(col, '#000000', 0.5));
    R(x - half + 3, yy - 3, Math.max(3, len >> 2), 1, '#c8c4bc');                          // the cabin top
    const mx = x - 1, tip = yy - mh;
    ctx.fillStyle = '#5a5e6c';
    for (let y2 = yy - 3; y2 > tip; y2--) ctx.fillRect(Math.round(mx + sway * (yy - 3 - y2) / mh * 0.8), y2, 1, 1);
    R(mx - half + 3, yy - 6, half + 1, 1, '#7a7e88');                                       // the boom with the sail furled on it
    const ax = Math.round(mx + sway * 0.8);
    ctx.globalAlpha = 0.4; ctx.fillStyle = '#8a8e9c';                                        // the forestay and the backstay
    for (let k = 1; k < mh - 3; k++) { const v = k / (mh - 3); ctx.fillRect(Math.round(lerp(ax, x + half - 1, v)), tip + k, 1, 1); if (k % 2) ctx.fillRect(Math.round(lerp(ax, x - half + 1, v)), tip + k, 1, 1); }
    ctx.globalAlpha = 0.75; dot(ax, tip, '#fff4dc'); ctx.globalAlpha = 1;                    // the anchor light, small at this distance
  });
}
// a sailing dinghy, seen side on: dir 1 heading right; sail 0..1 hoisted; s scale (1 near the float, ~0.35 far out)
function drawDinghy(x, y, s, dir, sail, col, lamp, t, seed) {
  const len = Math.max(5, Math.round(14 * s)), half = len >> 1, mh = Math.max(4, Math.round(20 * s)), xr = Math.round(x), yr = Math.round(y);
  ctx.globalAlpha = 0.25; R(xr - half, yr + 1, len, 1, '#8890b8'); ctx.globalAlpha = 1;
  R(xr - half, yr - 1, len, 1, col); R(xr - half + 1, yr, len - 2, 1, mixHex(col, '#000000', 0.45));
  if (s > 0.6) dot(dir > 0 ? xr + half - 1 : xr - half, yr - 2, col);                     // the bow's rise
  const mx = xr + dir * Math.round(len * 0.12);
  R(mx, yr - mh, 1, mh - 1, '#6a6e7a');
  if (sail > 0.02) {                                                                         // the main, on the leeward side of the mast
    const top = yr - 1 - Math.round((mh - 2) * sail), side = -dir;
    for (let y2 = top; y2 < yr - 1; y2++) {
      const w = Math.max(1, Math.round((y2 - top + 1) / (yr - 1 - top + 1) * len * 0.55));
      for (let k = 1; k <= w; k++) dot(mx + side * k, y2, k === w ? '#b8bcc8' : hash(seed, y2, k) < 0.15 ? '#d8d4cc' : '#f0ece2');
    }
  }
  if (lamp) { const lx = dir > 0 ? xr - half : xr + half - 1; dot(lx, yr - 2, '#fff4d8'); halo(HL.tiny, lx, yr - 2, 0.6); }
}
// three boats still beating about the basin, far out: each tacks at the ends of its beat (sails flip over)
const BEATERS = [{ x: 150, a: 70, k: 2, ph: 0.1, y: 171, s: 0.36, col: '#e8e4dc' }, { x: 330, a: 50, k: 3, ph: 0.6, y: 172, s: 0.42, col: '#2a3a6a' },
  { x: 240, a: 90, k: 1, ph: 0.35, y: 170, s: 0.32, col: '#e8e4dc' }];
function drawBeaters(t) {
  BEATERS.forEach((b, i) => {
    const a = TAU * (b.k * t / LOOP + b.ph), x = b.x + b.a * Math.sin(a), dir = Math.cos(a) >= 0 ? 1 : -1;
    const tack = Math.abs(Math.cos(a)) < 0.12;                                              // head to wind, sail luffing
    drawDinghy(x, b.y + 0.4 * wave(t, 40, i * 0.3), b.s, dir, tack ? 0.7 : 1, b.col, true, t, 50 + i);
  });
}
// the last four dinghies coming home together: far out at 176, tacking in, at the float at 212-222, then walked off right
const FLEET_B = [{ x0: 214, y0: 172, x1: 398, ph: 0, col: '#e8e4dc' }, { x0: 262, y0: 171, x1: 412, ph: 0.3, col: '#c83a2a' },
  { x0: 304, y0: 173, x1: 426, ph: 0.55, col: '#2a4a7a' }];
function fleetAt(f, i, t) {
  const { t0, dock } = FLEET, arrive = dock + i * 2.5, u = clamp((t - t0) / (arrive - t0), 0, 1), e = u * u * (3 - 2 * u) * 0.4 + u * 0.6;
  const y = lerp(f.y0, DOCK.y - 3, e * e), s = lerp(0.32, 1, (y - f.y0) / (DOCK.y - 3 - f.y0));
  const zig = Math.sin(TAU * (u * 3 + f.ph)) * 22 * (1 - u);
  let x = lerp(f.x0, f.x1, e) + zig;
  const dir = Math.cos(TAU * (u * 3 + f.ph)) * (1 - u) + 0.6 * Math.sign(f.x1 - f.x0) >= 0 ? 1 : -1;
  const sail = t < arrive - 1 ? 1 : 1 - smooth(arrive - 1, arrive + 4, t);
  if (t > arrive + 6) x += (t - arrive - 6) * 3.6;                                            // walked along the float to the racks
  return { x, y, s, dir: t > arrive ? 1 : dir, sail };
}
function drawFleet(t) {
  if (t < FLEET.t0 || t >= FLEET.t1) return;
  FLEET_B.forEach((f, i) => { const q = fleetAt(f, i, t); if (q.x < W + 10) drawDinghy(q.x, q.y, q.s, q.dir, q.sail, f.col, q.sail > 0.5, t, 60 + i); });
}
// the dinghies tied to the pavilion's float all evening, bobbing
const TIED = [[442, '#e8e4dc'], [462, '#c83a2a']];
function drawTied(t) {
  TIED.forEach(([x, col], i) => drawDinghy(x, DOCK.y - 3 + Math.round(0.6 * wave(t, 30, i * 0.37)), 1, 1, 0, col, false, t, 70 + i));
}
// the day's last Duck Boat: a boat-shaped hull on wheels, its canopy, riders, string lights; seen side on, bow left
const DUCK_ROWS = [
  '...wwwwwwwwwwwwwwwwww.', '...l.l...l...l...l.l..', '...p..p..p..p..p..p...', '..gggggggggggggggggg..', 'tggyyyyyyyyyyyyyyyyggg',
  '.ggggggggggggggggggggg', '..ggkkggggggggkkgggg..', '....kk........kk.....'];
const DUCK_KEY = { w: '#f0eee8', l: '#c8c4bc', p: '#e0b088', g: '#2a8a8a', y: '#f0c838', k: '#1a1a20', t: '#2a8a8a' };
function duckAt(t) {
  const u = (t - DUCK.t0) / (DUCK.t1 - DUCK.t0), e = u * 0.75 + u * u * 0.25;
  return { x: lerp(510, 98, e), y: lerp(187, 168.5, Math.pow(e, 0.7)), s: lerp(1.35, 0.42, Math.pow(e, 0.7)), a: 1 - smooth(0.93, 1, u) };
}
function drawDuck(t, crowd) {
  if (t < DUCK.t0 || t >= DUCK.t1) return;
  const { x, y, s, a } = duckAt(t), w = Math.round(22 * s), h = Math.round(8 * s), xr = Math.round(x), yr = Math.round(y);
  ctx.globalAlpha = a;
  drawWake(xr + w, yr, -1, Math.round(24 * s), t, 0.18 * s);
  ctx.globalAlpha = a * 0.28; ctx.drawImage(DUCK_C, 0, 3, 22, 4, xr, yr + 1, w, Math.max(1, Math.round(4 * s))); ctx.globalAlpha = a;
  ctx.drawImage(DUCK_C, xr, yr - h + 1, w, h);
  if (crowd > 0 && s > 0.6) for (let k = 0; k < 6; k++) {                                   // riders waving back at the kid
    const wv = inWin(t, WAVE[0], WAVE[1]) && (step(t, 480) + k) % 3 === 0;
    if (wv) dot(xr + Math.round((5 + k * 3) * s), yr - h + 1, '#e0b088');
  }
  for (let k = 0; k < 7; k++) { const bx0 = xr + Math.round((3 + k * 3) * s); if (hash(k, step(t, 240), 5) > 0.15) dot(bx0, yr - h + 1, k % 2 ? '#ffe6a0' : '#fff4d8'); }   // the canopy's bulbs
  dot(xr, yr - 3 * s, '#40ff70'); halo(HL.tiny, xr, yr - 3 * s, 0.7 * a);                  // starboard light (it heads left, away)
  ctx.globalAlpha = 1;
}
function drawWake(x, wy, dir, n, t, spread) {
  const st = step(t, 1440);
  for (let k = 0; k < n; k++) {
    if (hash(k, st, 17) > 0.84 - k * 0.01) continue;
    const ga = ctx.globalAlpha;
    ctx.globalAlpha = ga * 0.5 * (1 - k / n);
    dot(x - dir * k * 2, wy + 1 + k * spread, k < 5 ? '#dfe6f4' : '#8a98c0');
    if (spread > 0.1) dot(x - dir * k * 2, wy + 1 - k * spread * 0.4, '#6a78a0');
    ctx.globalAlpha = ga;
  }
}
// a rowing eight, right to left along the Cambridge side; a single sculler, left to right
function drawEight(t) {
  if (t < EIGHT.t0 || t >= EIGHT.t1) return;
  const u = (t - EIGHT.t0) / (EIGHT.t1 - EIGHT.t0), x = Math.round(lerp(500, -60, u)), y = 177, stroke = frac(t / 2.2);
  drawWake(x + 40, y, -1, 26, t, 0.14);
  ctx.globalAlpha = 0.3; R(x, y + 1, 40, 1, '#8890b8'); ctx.globalAlpha = 1;
  R(x, y, 40, 1, '#d8d0c0'); dot(x - 1, y, '#e8e0d0');
  const reach = stroke < 0.4 ? -1 : stroke < 0.55 ? 0 : 1;                                  // the catch, the drive, the recovery
  for (let k = 0; k < 8; k++) {
    const rx = x + 6 + k * 4;
    R(rx, y - 3, 1, 3, k % 2 ? '#e8e4dc' : '#c83a3a');                                       // the rower, in the club's colours
    dot(rx, y - 4, '#d8a880');
    const side = k % 2 ? 1 : -1;
    if (side < 0) { dot(rx + reach, y + 1, '#6a6458'); dot(rx + reach * 2, y + 2, '#8a8478'); }   // the near oars dip and sweep
  }
  R(x + 38, y - 2, 1, 2, '#3a3a44'); dot(x + 38, y - 3, '#d8a880');                          // the cox, in the stern
  if (stroke < 0.08) { ctx.globalAlpha = 0.5; dot(x + 8, y + 2, '#dfe6f4'); dot(x + 20, y + 2, '#dfe6f4'); ctx.globalAlpha = 1; }   // the puddles of the catch
}
function drawScull(t) {
  if (t < SCULL.t0 || t >= SCULL.t1) return;
  const u = (t - SCULL.t0) / (SCULL.t1 - SCULL.t0), x = Math.round(lerp(-30, 500, u)), y = 179, stroke = frac(t / 2.6);
  drawWake(x, y, 1, 18, t, 0.12);
  ctx.globalAlpha = 0.3; R(x, y + 1, 20, 1, '#8890b8'); ctx.globalAlpha = 1;
  R(x, y, 20, 1, '#e8e4dc');
  R(x + 9, y - 4, 2, 4, '#2a3a5a'); R(x + 9, y - 6, 2, 2, '#d8a880');
  const reach = stroke < 0.4 ? 1 : stroke < 0.55 ? 0 : -1;
  for (let k = 1; k <= 5; k++) dot(x + 10 - reach * 2 + Math.round(k * 0.4 * reach), y - 2 + k, '#8a8478');
}

/* ================= The Red Line on the Longfellow ================= */
// six silver cars with the red band, windows lit, crossing on the median between the towers: [s0, s1] along the bridge
function trainSpan(T, t) {
  const u = (t - T.t0) / (T.t1 - T.t0), len = 128, run = LF_LEN + len + 60;
  const front = T.dir > 0 ? -len + u * run : LF_LEN + 30 - u * run;
  return T.dir > 0 ? [front - len, front] : [front, front + len];
}
function drawTrain(t) {
  for (const T of TRAINS) {
    if (t < T.t0 || t >= T.t1) continue;
    const [s0, s1] = trainSpan(T, t);
    const xa = Math.round(lfAt(clamp(s0, 0, LF_LEN), 0, 9)[0]), xb = Math.round(lfAt(clamp(s1, 0, LF_LEN), 0, 9)[0]);
    for (let x = Math.max(34, xa); x <= Math.min(176, xb); x++) {
      const py = PARA_Y[x] || WL - 6, s = s0 + (s1 - s0) * (x - xa) / Math.max(1, xb - xa), car = Math.floor((s - s0) / 21.3);
      const gap = (s - s0) % 21.3 > 20.4;
      if (gap) continue;
      R(x, py - 4, 1, 1, '#a8b0bc'); R(x, py - 3, 1, 1, (x + car) % 3 === 0 ? '#c8ccd4' : '#ffe8c0'); R(x, py - 2, 1, 1, '#d02a2a');
    }
    const head = T.dir > 0 ? xb : xa, tail = T.dir > 0 ? xa : xb, sf = T.dir > 0 ? s1 : s0, sb = T.dir > 0 ? s0 : s1;
    if (sf >= 0 && sf <= LF_LEN && head >= 34) { dot(head, PARA_Y[head] - 3, '#ffffff'); halo(HL.head, head + T.dir * 2, PARA_Y[head] - 3, 0.9); }
    if (sb >= 0 && sb <= LF_LEN && tail >= 34) dot(tail, PARA_Y[tail] - 2, '#ff3a30');
  }
}

/* ================= Aircraft: jets on approach to Logan, beyond downtown; airliners high over ================= */
function drawApproach(t) {
  for (const P of JETS_IN) {
    if (t < P.t0 || t >= P.t1) continue;
    const u = (t - P.t0) / (P.t1 - P.t0), x = Math.round(lerp(470, 196, u)), y = Math.round(lerp(58, 128, u * 0.4 + u * u * 0.6));
    halo(HL.land, x - 1, y, 0.85);
    dot(x - 1, y, '#ffffff'); R(x, y, 3, 1, '#9aa0b0');
    if (frac(t * 0.9 + u) < 0.12) dot(x + 2, y - 1, '#ff4a40');
  }
}
// little brown bats out over the water at dusk, hawking insects: looping, never still
const BATS = [{ cx: 120, cy: 138, ax: 70, ay: 16, kx: 7, ky: 17, ph: 0.1 }, { cx: 250, cy: 126, ax: 46, ay: 14, kx: 9, ky: 19, ph: 0.45 },
  { cx: 170, cy: 150, ax: 56, ay: 10, kx: 8, ky: 21, ph: 0.8 }];
function drawBats(t) {
  BATS.forEach((b, i) => {
    const x = Math.round(b.cx + b.ax * Math.sin(TAU * (b.kx * t / LOOP + b.ph)) + 7 * Math.sin(TAU * (b.kx * 5 * t / LOOP + i))), y = Math.round(b.cy + b.ay * Math.sin(TAU * (b.ky * t / LOOP + b.ph * 2)));
    ctx.fillStyle = '#0a0c1e';
    if ((step(t, 2880) + i) & 1) { ctx.fillRect(x - 1, y - 1, 1, 1); ctx.fillRect(x + 1, y - 1, 1, 1); ctx.fillRect(x, y, 1, 1); }
    else ctx.fillRect(x - 1, y, 3, 1);
  });
}
// cars on Memorial Drive, glimpsed under the bank's trees on the left: headlights coming, tail lights going
function drawMemDrive(t) {
  for (let i = 0; i < 4; i++) {
    const come = i & 1, u = frac(t * (come ? 5 : 4) / LOOP * 2 + i * 0.27), x = Math.round(come ? lerp(34, -2, u) : lerp(-2, 34, u));
    if (x < 0 || x > 34 || hash(x >> 1, 7, 51) < 0.35) continue;
    const y = Math.round(lerp(196, 169, x / 37)) - 4;
    dot(x, y, come ? '#fff2d8' : '#ff4a3a');
    if (come && x < 14) dot(x + 1, y, '#fff2d8');
  }
}
const JETS = [{ k: 4, ph: 0.1, y0: 14, y1: 26, dir: -1 }, { k: 3, ph: 0.55, y0: 6, y1: 3, dir: 1 }];
function drawJets(t) {
  for (const j of JETS) {
    const u = frac(j.k * t / LOOP + j.ph);
    if (u > 0.4) continue;
    const v = u / 0.4, x = j.dir > 0 ? lerp(-10, W + 10, v) : lerp(W + 10, -10, v), y = lerp(j.y0, j.y1, v);
    dot(x, y, '#c8ccd8'); if (frac(t * 0.9 + j.ph) < 0.1) dot(x, y + 1, '#ff4a40');
    if (frac(t * 0.7 + j.ph * 3) < 0.06) dot(x + 1, y, '#ffffff');
  }
}

/* ================= The Cambridge side: the stone wall, the riverside path, the lawn ================= */
const PAVE = ['#14161f', '#1b1e29', '#242834', '#2e3240', '#3a3e4c'];
const LAWN = ['#08100a', '#0d170f', '#132014', '#1a2a1a', '#223422'];
const WARMG = ['#3a3430', '#5a4c3e', '#806a4e', '#a88a5e', '#d0ae76', '#ecd09a'];
const GRANITE_C = ['#2a2c36', '#3a3c48', '#4c4e5a', '#60626c'];
function groundLight(x, y) {
  let s = 0;
  for (const L of LAMPS) s += 0.6 * Math.exp(-(((x - L.x) / 36) ** 2) - (((y - 214) / 13) ** 2));
  s += 0.55 * Math.exp(-(((x - SHACK.cx) / 30) ** 2) - (((y - 218) / 10) ** 2));            // the shack's window
  return Math.min(1, s);
}
function renderGround() {
  const Y0 = QY - 1, p = pix(W, H - Y0), put = (x, y, c) => p.set(x, y - Y0, c);
  for (let x = 0; x < W; x++) {
    put(x, QY - 1, hash(x >> 1, 3, 21) < 0.5 ? '#6c6a78' : '#5a5866');                       // the cap's far edge, catching the sky
    for (let y = QY; y <= QY + 2; y++) {
      const g = groundLight(x, y), joint = x % 23 === 0;
      put(x, y, joint ? '#15161d' : g > 0.25 ? pick(WARMG, 0.3 + g * 0.5 - (y - QY) * 0.08, x, y) : pick(GRANITE_C, 0.75 - (y - QY) * 0.25, x, y));
    }
    put(x, QY + 3, '#0c0d14');
    for (let y = QY + 4; y < H; y++) {
      const g = groundLight(x, y), edgeG = GRASS + Math.round(2 * vnoise(x / 7, 1, 27) - 1);
      let col;
      if (y < edgeG) {
        const v = 0.42 + (vnoise(x / 14, y / 3, 23) - 0.5) * 0.16 + (hash(x, y, 24) - 0.5) * 0.08 + (y - QY) * 0.004;
        col = g + (bayer(x, y) - 0.5) * 0.3 > 0.36 ? pick(WARMG, 0.06 + g * 0.6 + (v - 0.4) * 0.5, x, y, 0.6) : pick(PAVE, v + g * 0.3, x, y, 0.6);
      } else {
        const v = 0.38 + (vnoise(x / 6, y / 2, 25) - 0.5) * 0.3 + (y - GRASS) * 0.005 + (y === edgeG ? -0.2 : 0);
        col = pick(LAWN, v + g * 0.5, x, y, 0.8);
        if (g > 0.35 && hash(x, y, 26) < g * 0.3) col = '#344a2a';
      }
      put(x, y, col);
    }
  }
  for (let k = 0; k < 220; k++) {                                                            // a few early plane-tree leaves on the path
    const x = Math.floor(hash(k, 1, 91) * W), y = QY + 5 + Math.floor(hash(k, 2, 91) * (H - QY - 6));
    put(x, y, mixHex(hash(k, 3, 91) < 0.5 ? '#6a6a2a' : '#8a6a2a', '#0a0c18', 0.5 - groundLight(x, y) * 0.3));
  }
  return { c: p.done(), x: 0, y: Y0 };
}
/* a riverside lamp post: a slim black pole, a lantern with a cap */
function renderLamp() {
  const p = pix(9, 66), K = '#0e1016', KH = '#2e3240', cx = 4;
  for (let y = 10; y < 64; y++) { p.set(cx, y, K); p.set(cx - 1, y, y < 36 ? KH : '#1c1e28'); }
  for (let y = 58; y < 66; y++) { const hw = y < 61 ? 1 : 2; for (let x = cx - hw; x <= cx + hw; x++) p.set(x, y, x === cx - hw ? KH : K); }
  for (let x = cx - 3; x <= cx + 3; x++) p.set(x, 1, K);
  for (let x = cx - 2; x <= cx + 2; x++) { p.set(x, 0, K); p.set(x, 8, K); p.set(x, 9, KH); }
  for (let y = 2; y < 8; y++) for (let x = cx - 2; x <= cx + 2; x++) p.set(x, y, Math.abs(x - cx) === 2 ? K : y < 4 ? '#fffbea' : y < 6 ? '#fff0c8' : '#f0d090');
  return p.done();
}
/* a park bench, seen from behind */
function renderBench() {
  const p = pix(32, 14), K = '#101218', WOOD = '#3e2c20', WOODL = '#5e4430', WOODH = '#7a5a3e';
  for (let x = 1; x < 31; x++) { p.set(x, 0, WOODH); p.set(x, 1, WOOD); p.set(x, 3, WOODL); p.set(x, 4, WOOD); p.set(x, 7, WOODL); }
  for (const lx of [2, 29]) for (let y = 0; y < 14; y++) { p.set(lx, y, K); if (y > 8) p.set(lx + (lx < 10 ? -1 : 1), y, '#22242e'); }
  return p.done();
}
/* Memorial Drive's sycamores (London planes): leaning trunks of mottled bark, cream and olive patches, big crowns */
const SYC = ['#0a0f0a', '#101810', '#182416', '#22301c', '#2e3e22', '#3e5028', '#566a32'];
const BARK = ['#1a1a14', '#2a2a20', '#3e3c2c', '#5a5640', '#7a7458', '#9a9478'];
const SYC_TIPS = [];
function renderSycamore(side) {
  const p = pix(W, H), T = side < 0 ? SYC_L : SYC_R, r = rng(side < 0 ? 811 : 812);
  for (let y = 30; y <= T.base; y++) {
    const u = (T.base - y) / (T.base - 30), w = Math.round(lerp(19, 11, u)), x0 = Math.round(T.x - (w >> 1) - side * 10 * u * u + Math.sin(y * 0.05) * 1.5);
    for (let x = x0; x < x0 + w; x++) {
      const patch = vnoise(x / 2.2, y / 3.5, side < 0 ? 801 : 802);
      let v = patch > 0.62 ? 0.85 : patch > 0.45 ? 0.55 : 0.25;
      if (x === x0) v -= 0.15; if (x === x0 + w - 1) v -= 0.3;
      const lamp = side < 0 ? 0.12 : 0.18;                                                  // the lamps light the side facing the path
      p.set(x, y, pick(BARK, v * (0.55 + lamp) - (y > T.base - 6 ? 0.1 : 0), x, y, 0.6));
    }
  }
  for (let y = T.base - 3; y <= T.base; y++) for (let x = T.x - 9; x <= T.x + 9; x++) if (Math.abs(x - T.x) < 5 + (y - T.base + 3) * 1.6) p.set(x, y, '#0e0c08');
  const limbs = side < 0 ? [[T.x + 4, 80, T.x + 60, 20], [T.x, 60, T.x - 20, 4], [T.x + 6, 110, T.x + 90, 50], [T.x + 2, 50, T.x + 36, -6]]
    : [[T.x - 4, 70, T.x - 50, 14], [T.x, 50, T.x + 20, -4], [T.x - 3, 100, T.x - 40, 40]];
  const blobs = [];
  for (const [x0, y0, x1, y1] of limbs) {
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
    for (let k = 0; k <= n; k++) {
      const u = k / n, x = lerp(x0, x1, u) + Math.sin(u * 5 + x0) * 1.2, y = lerp(y0, y1, u), w = Math.max(2, Math.round(7 * (1 - u * 0.7)));
      for (let i = 0; i < w; i++) p.set(x + i, y, pick(BARK, i === 0 ? 0.7 : vnoise((x + i) / 2, y / 3, 803) > 0.6 ? 0.55 : 0.22, x + i, y));
      if (k % 10 === 5) blobs.push({ x: x + (r() - 0.5) * 12, y: y - 4 - r() * 8, rr: 12 + r() * 9 });
    }
    blobs.push({ x: x1, y: y1 - 4, rr: 14 + r() * 8 });
  }
  const X0 = side < 0 ? 0 : 400, X1 = side < 0 ? 130 : W, Y1 = side < 0 ? 96 : 64;
  for (let y = 0; y < Y1; y++) for (let x = X0; x < X1; x++) {
    let best = 0, by = 0;
    for (const b of blobs) { const d = 1 - Math.hypot(x - b.x, (y - b.y) * 1.2) / b.rr; if (d > best) { best = d; by = b.y; } }
    const n = vnoise(x / 3, y / 3, 813 + side), n2 = vnoise(x / 8, y / 7, 815 + side), edge = best + (n - 0.5) * 0.5;
    if (edge < 0.08) continue;
    if (n2 > 0.74 && best < 0.45 && n > 0.5) continue;
    const under = clamp((y - by) / 12, -1, 1);
    const v = 0.38 + under * 0.14 + (n - 0.5) * 0.5 + (n2 - 0.5) * 0.3 - (y < 18 ? 0.1 : 0) - (edge < 0.2 ? 0.1 : 0);
    p.set(x, y, pick(SYC, v, x, y, 0.8));
    if (hash(x, y, 818) < 0.18) SYC_TIPS.push({ x, y, ph: hash(x, y, 819), k: 24 + Math.floor(hash(x, y, 820) * 40), col: pick(SYC, v + 0.3, x, y) });
  }
  return trim(p);
}
/* ================= Pinchy's lobster shack, and its sign (the city's billboard) ================= */
// A weathered cedar-shingle shack, white trim, a red-and-white awning over its lit order window, buoys and a trap on
// its side; on its roof the hand-painted sign, lit by two gooseneck lamps. The sign, its frame, posts and lamps are the
// billboard; the shack stays without it.
const SHINGLE = ['#22201e', '#34302a', '#4a4438', '#625a4a', '#7e745e'];
function renderShack() {
  const { x0, x1, roof, eave, base } = SHACK, p = pix(W, H);
  for (let y = roof; y <= eave; y++) {
    const inset = Math.round((eave - y) * 0.8);
    for (let x = x0 - 2 + inset; x <= x1 + 2 - inset; x++) {
      let v = 0.3 + (y - roof) * 0.05 + ((x + y) % 3 === 0 ? 0.08 : 0);
      if (y === roof) v = 0.6; if (y === eave) v = 0.12;
      p.set(x, y, pick(['#1a1416', '#2a1e1e', '#3c2a26', '#523a30'], v, x, y, 0.6));
    }
  }
  for (let y = eave + 1; y < base; y++) for (let x = x0; x <= x1; x++) {
    const hl = Math.exp(-(((x - SHACK.cx) / 30) ** 2) - (((y - 190) / 16) ** 2));
    let v = 0.26 + 0.45 * hl + ((y - eave) % 2 === 0 ? -0.1 : 0) + (hash(x >> 1, y >> 1, 77) - 0.5) * 0.14;
    if (x === x0 || x === x1) v = 0.8;                                                      // the white corner boards
    p.set(x, y, x === x0 || x === x1 ? pick(['#5a5a60', '#8a8a90', '#c8c8cc'], v * 0.7 + hl * 0.3, x, y) : pick(SHINGLE, v, x, y, 0.6));
  }
  const { x0: wx0, x1: wx1, y0: wy0, y1: wy1 } = WIN;
  for (let y = wy0; y <= wy1; y++) for (let x = wx0; x <= wx1; x++) {                       // the window: the lit kitchen, the fryer, the rolls
    let col = pick(['#a8682e', '#c88840', '#e8b064', '#f8d090'], 0.25 + 0.6 * smooth(wy1, wy0, y) - Math.abs(x - SHACK.cx) * 0.01, x, y);
    if (y === wy0 + 4) col = '#8a5a30';
    if (y === wy0 + 3 && x % 3 === 0) col = ['#c83a2a', '#e8d070', '#3a6a8a'][x % 9 / 3 | 0];
    if (x >= wx1 - 9 && x <= wx1 - 4 && y >= wy0 + 7) col = x === wx1 - 9 ? '#d8dce4' : '#8a909a';   // the fryer
    p.set(x, y, col);
  }
  for (let x = wx0 - 3; x <= wx1 + 3; x++) {
    for (let y = wy0 - 5; y < wy0; y++) p.set(x, y, ((x - wx0 + 40) >> 1) % 2 ? '#e8e0d0' : '#b82a2a');   // the awning
    for (let k = 0; k < 2; k++) if ((x - wx0) % 4 === 0) p.set(x, wy0 - 0 + k * 0, '#b82a2a');
    p.set(x, wy0 - 5, '#f4ece0'); p.set(x, wy1 + 1, '#c8c0b8'); p.set(x, wy1 + 2, '#5a5458');
  }
  for (let y = 190; y < 204; y++) for (let x = x1 - 9; x < x1 - 2; x++) p.set(x, y, y === 190 || y === 203 ? '#3a2c22' : '#1e2a22');   // the chalk menu
  for (const [x, y] of [[x1 - 8, 193], [x1 - 7, 193], [x1 - 5, 193], [x1 - 8, 196], [x1 - 6, 196], [x1 - 4, 196], [x1 - 8, 199], [x1 - 7, 199]]) p.set(x, y, '#c8d0c0');
  for (const [bx0, by0, col] of [[x0 + 2, 166, '#d84a2a'], [x0 + 3, 172, '#e8d040'], [x0 + 2, 178, '#e8e8e0']]) {   // buoys on the corner
    p.fill(bx0, by0, 3, 4, col); p.set(bx0 + 1, by0 - 1, '#4a4038');
  }
  for (let y = 198; y < 206; y++) for (let x = x0 - 8; x < x0 - 1; x++) p.set(x, y, (x + y) % 2 ? '#3a3a2a' : '#6a5a3a');   // a lobster trap by the door
  return trim(p);
}
function renderBoard() {
  const p = pix(W, H), L = BB.x - 2, Rr = BB.x + BB.w + 1, T0 = BB.y - 2, B = BB.y + BB.h + 1;
  for (const lx of [BB.x + 8, BB.x + BB.w - 9]) for (let y = B; y <= SHACK.roof + 2; y++) { p.set(lx, y, '#1a1c24'); p.set(lx + 1, y, '#0e1016'); }
  for (let y = T0; y <= B; y++) for (let x = L; x <= Rr; x++) {
    let col = '#22242e';
    if (y === T0 || x === L) col = '#4a4c58'; else if (y === B || x === Rr) col = '#121318';
    p.set(x, y, col);
  }
  for (const lx of [BB.x + 10, BB.x + BB.w - 11]) {
    p.line(lx, T0, lx, T0 - 4, '#2a2c36'); p.line(lx, T0 - 4, lx + 3, T0 - 5, '#2a2c36');
    p.set(lx + 3, T0 - 4, '#3a3c48'); p.set(lx + 4, T0 - 4, '#3a3c48');
  }
  return trim(p);
}
// the lobster, red on cream, claws up
const LOBSTER = ['r.....r...', 'rr...rr...', '.rr.rr....', '..rrrr....', '..rRRr....', '...rr.....', '..rRRr....', '..rrrr....', '...rr.....', '..r..r....', '.rrrrrr...'];
function renderAd() {
  const { w, h } = BB, p = pix(w, h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) p.set(i, j, pick(['#c8bc9c', '#e0d4b0', '#f0e6c4'], 0.85 - j * 0.02 + (i < 13 ? -0.06 : 0), i, j));
  for (let i = 0; i < w; i++) { p.set(i, 0, '#1e3a6a'); p.set(i, h - 1, '#1e3a6a'); }
  const key = { r: '#c82a1e', R: '#8a1a14' };
  LOBSTER.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (key[row[i]]) p.set(2 + i, 4 + j, key[row[i]]); });
  const width = s => [...s].reduce((n, ch) => n + (FONTS[ch]?.[0].length ?? 3) + 1, 0) - 1;
  const ink = (s, y, col) => { let xx = 13 + Math.floor((w - 14 - width(s)) / 2); for (const ch of s) { const g = FONTS[ch]; if (g) g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') p.set(xx + i, y + j, col); }); xx += (g?.[0].length ?? 3) + 1; } };
  ink("PINCHY'S", 2, '#b8241a');                    // each line centred right of the lobster
  ink('LOBSTAH', 8, '#1e2e5a');
  ink('CHOWDAH', 14, '#1e2e5a');
  return gradeAd(p.done(), { level: 0.72, tint: '#141a3a', mix: 0.14, wear: 0.3, seed: 9 });
}
// the letters the sign spells (the 3x5 font's, written out: the ad is baked once)
const FONTS = {
  P: ['110', '101', '110', '100', '100'], I: ['111', '010', '010', '010', '111'], N: ['1001', '1101', '1011', '1001', '1001'],
  C: ['011', '100', '100', '100', '011'], H: ['101', '101', '111', '101', '101'], Y: ['101', '101', '010', '010', '010'],
  "'": ['1', '1', '0', '0', '0'], S: ['011', '100', '010', '001', '110'], L: ['100', '100', '100', '100', '111'],
  O: ['010', '101', '101', '101', '010'], B: ['110', '101', '110', '101', '110'], T: ['111', '010', '010', '010', '010'],
  A: ['010', '101', '111', '101', '101'], W: ['10001', '10001', '10101', '10101', '01010'], D: ['110', '101', '101', '101', '110'],
};
/* the pavilion's last float, out to the right edge: grey decking, cleats, a dock light on a post */
function renderDock() {
  const p = pix(W, H), y = DOCK.y;
  for (let x = DOCK.x0; x < W; x++) {
    p.set(x, y, '#8a8a90'); p.set(x, y + 1, (x % 6 === 0) ? '#3a3a40' : '#5a5a62'); p.set(x, y + 2, '#2a2a30'); p.set(x, y + 3, '#16161c');
    if (x % 14 === 3) p.set(x, y - 1, '#c8c8d0');                                           // cleats
  }
  for (let yy = y - 6; yy < y + 4; yy++) { p.set(DOCK.x0 + 2, yy, '#2e2a26'); p.set(DOCK.x0 + 3, yy, '#1e1a18'); }   // a piling (the heron's)
  p.set(DOCK.x0 + 2, y - 7, '#4a4440'); p.set(DOCK.x0 + 3, y - 7, '#3a3430');
  for (let yy = y - 9; yy < y; yy++) p.set(454, yy, '#2a2c34');                                // the dock light's post
  p.set(453, y - 10, '#3a3c44'); p.set(454, y - 10, '#3a3c44'); p.set(455, y - 10, '#3a3c44');
  return trim(p);
}
// a picked weather (site/js/engine/weather.js): the city's own is clear; rain and snow land on the basin, the path and
// past the viewer, and catch the lamps and the shack's window (and the sign's lamps, while it's there)
const WEATHER = weatherPlans([]);
const WX_LAND = [[WL + 2, QY - 1], [QY + 4, GRASS + 4], [H + 4, H + 4]];
const WX_LIGHTS = [...LAMPS.map(l => ({ x: l.x, y: l.y + 22, rx: 30, ry: 46 })), { x: SHACK.cx, y: 180, rx: 26, ry: 18 }];
const WX = { land: WX_LAND, lights: WX_LIGHTS };
const WX_BB = { land: WX_LAND, lights: [...WX_LIGHTS, { x: BB.x + 23, y: BB.y + 8, rx: 30, ry: 16, col: '#ffe8c0' }] };

/* ================= People ================= */
// One function draws everyone (after rome.js and toronto.js): a look gives the proportions for a height and the colours
// dimmed for the night; person() puts it on its feet. Late-August clothes: T-shirts, shorts, sundresses, a light jacket.
// Seen from behind, the shoulders and the hair catch the city's warm glow across the water; the lamps warm the rest.
const SKINS = ['#f2c8a0', '#e2ac82', '#c48c60', '#8c5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#d0a858', '#96421c', '#c8c4c0'];
const NIGHT = '#0c1030', GLOWC = '#e8c8c8';
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.3, m = c => mixHex(c, NIGHT, dk);
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.38);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 24 ? 7 : h >= 19 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOWC, 0.3), top: m(top), topD: mixHex(top, NIGHT, dk + 0.3),
    topL: mixHex(top, GLOWC, 0.3), bot: m(bot), shoe: m('#1a161c'), long: o.long || 0, coat: o.coat || 0, shorts: o.shorts || 0,
    hat: o.hat ? m(o.hat) : null, dress: o.dress || 0 };
}
/**
 * One person, feet on row fy, centred on column x; returns the top row of the head.
 * o.view: 0 from behind (looking at the view), 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame
 * o.aL / o.aR: 0 down, 1 straight up, 2 bent up to the face (phone, roll), 3 out, 4 up and out (pointing), 5 round a
 * neighbour's back; o.lean: the head towards a neighbour; o.sit: seated (on a bench or the wall); o.bend: bent forward
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll, bend = side && o.bend ? 1 : 0;
  const ty = fy - legs - th + 1 + bend, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1) + bend * dir;
  if (legs) {
    const ly = fy - legs + 1, low = P.shorts ? Math.ceil(legs * 0.55) : 0, legC = P.dress ? P.top : P.bot;
    ctx.fillStyle = 'rgba(8,10,24,0.34)'; ctx.fillRect(x - (bw >> 1) - 2, fy + 1, bw + 4, 1); ctx.fillRect(x - (bw >> 1), fy + 2, bw, 1);
    const leg = (lx, ly0, n) => { R(lx, ly0, 1 + (side ? 1 : 0), n, legC); if (low) R(lx, fy - low + 1, 1 + (side ? 1 : 0), low - 1, P.skin); };
    if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, legC);
        leg(dir > 0 ? x + 1 : x - 2, ly + 2, legs - 2); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        leg(dir > 0 ? x - 2 : x + 1, ly + 2, legs - 3); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { leg(x - 1, ly, legs); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, legC); R(tx, ly + 2, lw, legs - 2, legC); R(tx + bw - lw, ly + 2, lw, legs - 2, legC);
      if (low) { R(tx, fy - low + 1, lw, low - 1, P.skin); R(tx + bw - lw, fy - low + 1, lw, low - 1, P.skin); }
      if (P.dress) R(tx - 1, ly, bw + 2, 3, P.top);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
    }
  } else if (o.sit) { R(tx - 1, fy - 1, bw + 2, 2, P.bot); if (o.dangle) { R(tx, fy + 1, 2, 4, P.shorts ? P.skin : P.bot); R(tx + bw - 2, fy + 1, 2, 4, P.shorts ? P.skin : P.bot); } }
  R(tx, ty, bw, th + P.coat, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th + P.coat - 1, P.topD);
  else R(dir > 0 ? tx : tx + bw - 1, ty + 1, 1, th + P.coat - 1, P.topD);
  if (view !== 1) R(tx, ty, bw, 1, P.topL);                                            // the city's glow on the shoulders
  const skinArm = !P.coat;                                                              // T-shirts: bare forearms
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1, 1, th - 2, skinArm ? P.skin : P.topD); R(ax, ty + 1, 1, 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 1) { R(ax, hy - 1, 1, ty - hy + 2, P.topD); R(ax, hy - 3, 1, 2, P.skin); }
    else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
    else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); }
    else if (st === 4) { for (let k = 0; k < 4; k++) R(ax + sg * k, ty + 1 - k, 1, 1, P.topD); R(ax + sg * 4, ty - 3, 1, 1, P.skin); }
    else { R(sg < 0 ? ax - 4 : ax, ty + 2, 5, 1, P.topD); }
  };
  if (side) {
    const st = o.aR || 0, ax = x + (o.step ? -dir : 0) + bend * dir * 2;
    if (!st) { R(ax, ty + 1, 1, th - 2, skinArm ? P.skin : P.topD); R(ax, ty + 1, 1, 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 2) { for (let k = 0; k < 3; k++) R(x + dir * (1 + k) + bend * dir, ty + 2 - k, 1, 1, P.topD); R(x + dir * 4 + bend * dir, ty - 1, 1, 1, P.skin); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else if (st === 1) { R(x + dir, hy - 1, 1, ty - hy + 2, P.topD); R(x + dir, hy - 3, 1, 2, P.skin); }
    else { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, ty, 1, 1, P.skin); }
  } else { arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1) + (o.lean || 0) + bend * dir * 2, hr = hh >= 5 ? 2 : 1, hY = hy + bend;
  if (view === 0) {
    R(hx, hY, hw, hh, P.hair); R(hx, hY, hw, 1, P.hairL);
    if (P.long) R(hx, hY + hh, hw, P.long, P.hair); else R(hx + 1, hY + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hY, hw, hh, P.skin); R(hx, hY, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hY + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hY + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hY, hw, hh, P.skin); R(hx, hY, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hY, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) { R(hx, hY - 1, hw, 2, P.hat); if (side) R(dir > 0 ? hx + hw : hx - 1, hY, 1, 1, P.hat); }   // a ball cap, its peak in profile
  return hY;
}
const tyOf = (P, fy) => fy - P.ll - P.th + 1;
const L_HE = look(22, 1, 0, '#3a5a7a', '#2a2a36', { coat: 1 });
const L_SHE = look(21, 0, 4, '#d88a6a', '#2a2a3a', { long: 3, dress: 1 });
const L_PARENT = look(23, 2, 0, '#5a7a4a', '#2a2a30', { shorts: 1 });
const L_KID = look(14, 2, 0, '#e8c030', '#2a3a6a', { shorts: 1, dk: 0.22 });
const L_FISH = look(23, 1, 5, '#6a6a5a', '#3a3a30', { hat: '#1e3a6a', coat: 1 });
const L_FRA = look(21, 3, 0, '#c8323a', '#2a2a3a', { shorts: 1 }), L_FRB = look(21, 0, 3, '#e8e4dc', '#3a4a6a', { long: 2 });
const L_PHONE = look(22, 1, 1, '#8a5a9a', '#1e2030', { long: 3, dk: 0.26 });
const L_DOGW = look(26, 0, 2, '#4a6a8a', '#c8b898', { shorts: 1, hat: '#c8323a', dk: 0.32 });
const L_JOG = look(27, 2, 0, '#e8e050', '#1a1c28', { shorts: 1, dk: 0.32 }), L_JOG2 = look(26, 1, 4, '#e86a8a', '#1a1c28', { shorts: 1, long: 2, dk: 0.32 });
const L_STROLL_A = look(26, 3, 0, '#e8e4dc', '#3a4a6a', { dk: 0.32 }), L_STROLL_B = look(25, 1, 3, '#4a8a7a', '#2a2a38', { long: 3, dress: 1, dk: 0.32 });
const L_BIKE = [look(24, 0, 4, '#3a8a5a', '#2a2a38', { hat: '#e8e4dc', dk: 0.3 }), look(24, 2, 0, '#c84a3a', '#1e2030', { dk: 0.3 }), look(23, 1, 3, '#3a7aa8', '#2a2a38', { long: 2, dk: 0.3 })];
const L_SAILOR = look(18, 1, 3, '#e8e4dc', '#2a3a6a', { shorts: 1, hat: '#8a1a1a', dk: 0.26 });
const CUSTOMERS = [look(25, 0, 1, '#5a8a4a', '#2a2a30', { shorts: 1, dk: 0.32 }), look(24, 3, 0, '#e8d8b8', '#2a3a5a', { dk: 0.32 }),
  look(25, 1, 4, '#c86a3a', '#2a2a38', { long: 3, dress: 1, dk: 0.32 }), look(26, 2, 0, '#2a3a5a', '#c8b898', { shorts: 1, hat: '#1e2e5a', dk: 0.32 }),
  look(24, 0, 3, '#8a3a6a', '#1e2030', { long: 2, dk: 0.32 })];
const VENDOR = look(20, 1, 1, '#e8e4dc', '#2a2a30', { hat: '#b82a2a', dk: 0.12 });
const SPOTS = { kid: 74, parent: 63, friends: [112, 122], fisher: 166, couple: [210, 221], phone: 336 };
/* a bicycle under its rider, in profile, its lamp lit */
function drawBike(x, fy, dir, P, t) {
  const wy = fy - 3, fx = x + dir * 5, bx0 = x - dir * 5, spin = step(t, 960) & 1;
  for (const cx of [fx, bx0]) {
    ctx.fillStyle = '#16181f';
    ctx.fillRect(cx - 1, wy - 3, 3, 1); ctx.fillRect(cx - 1, wy + 3, 3, 1); ctx.fillRect(cx - 3, wy - 1, 1, 3); ctx.fillRect(cx + 3, wy - 1, 1, 3);
    ctx.fillRect(cx - 2, wy - 2, 1, 1); ctx.fillRect(cx + 2, wy - 2, 1, 1); ctx.fillRect(cx - 2, wy + 2, 1, 1); ctx.fillRect(cx + 2, wy + 2, 1, 1);
    dot(cx + (spin ? 1 : -1), wy + (spin ? -1 : 1), '#5a5c66');
  }
  ctx.fillStyle = 'rgba(8,10,24,0.34)'; ctx.fillRect(Math.min(fx, bx0) - 3, fy + 1, 14, 1);
  ctx.fillStyle = '#2a5a8a';
  for (let k = 0; k <= 5; k++) ctx.fillRect(Math.round(bx0 + dir * k), Math.round(wy - k * 0.6), 1, 1);
  for (let k = 0; k <= 4; k++) ctx.fillRect(Math.round(x + dir * k), wy - 3, 1, 1);
  R(fx - dir, wy - 5, 1, 2, '#16181f'); R(fx - dir * 2, wy - 5, 2, 1, '#16181f');
  R(x - 1, wy - 4, 3, 1, '#0e0f14');
  dot(fx + dir, wy - 4, '#fff4d0'); halo(HL.tiny, fx + dir * 2, wy - 4, 0.9);
  ctx.globalAlpha = 0.16; ctx.fillStyle = '#fff4d0';                                        // the lamp's beam on the path
  for (let k = 2; k < 14; k++) ctx.fillRect(fx + dir * k, wy - 3 + (k >> 2), 1, 1 + (k >> 3));
  ctx.globalAlpha = 1;
  dot(bx0 - dir * 3, wy - 2, '#ff3a30');
  const ty = wy - 4 - P.th;
  R(x - 2, ty, P.tw - 2, P.th, P.top); R(x - 2, ty, P.tw - 2, 1, P.topL);
  R(x + dir * 2, ty + 2, 1, 1, P.topD); R(x + dir * 3, ty + 3, 1, 1, P.topD); R(fx - dir * 2, ty + 4, 1, 1, P.skin);
  R(x - 1, wy - 3, 2, 3, P.bot); R(x + (spin ? dir : -dir), fy - 2, 1, 2, P.bot);
  const hx = x - 1 + dir, hy = ty - P.hh;
  R(hx, hy, P.hw, P.hh, P.skin); R(hx, hy, P.hw, 2, P.hair); R(dir > 0 ? hx : hx + P.hw - 1, hy, 1, P.hh - 1 + P.long, P.hair);
  R(hx - 1, hy - 1, P.hw + 2, 2, P.hat || '#2a2c34');                                        // a helmet
}
const DOG_ROWS = [
  ['........ee..', '.......edddn', 't......ddd..', '.tlllllllll.', '..dddddddd..', '..d.d..d.d..', '..d.d..d.d..'],
  ['........ee..', '.......edddn', 't......ddd..', '.tlllllllll.', '..dddddddd..', '...dd..dd...', '..d..d.d..d.'],
  ['............', '........ee..', 't......eddd.', '.tllllllldn.', '..dddddddd..', '..d.d..d.d..', '..d.d..d.d..'],
];
const DOG_KEY = { d: '#8a6a3a', l: '#b08a50', t: '#8a6a3a', e: '#5a4020', n: '#1a1008' };
/* mallards near the wall: a drake (green head) and two hens, paddling; and the black-crowned night heron on the post */
const DRAKE = ['..gg....', '.ggw....', '..wbbbb.', '.bbbbbbt', '..pppp..'], HEN = ['..hh....', '.hhh....', '..hhhhh.', '.hHhhhht', '..hhhh..'];
const DUCK_K = { g: '#1e5a3a', w: '#e8e8e0', b: '#8a8a90', t: '#2a2a30', p: '#6a4a3a', h: '#7a5a3a', H: '#5a3e28' };
const MALLARDS = [{ x: 150, a: 40, k: 1, ph: 0.2, y: 194, drake: true }, { x: 160, a: 34, k: 1, ph: 0.25, y: 196, drake: false }, { x: 236, a: 22, k: 2, ph: 0.7, y: 192, drake: false }];
// the night heron: black cap and back, grey wings, white face and breast, yellow legs; hunched, then the neck shoots out
const HERON_ROWS = { stand: ['..kk...', '.kkkk..', 'wwkkkk.', '.wkggg.', '.wwggg.', '..wgg..', '...y...', '..y.y..'],
  strike: ['.......', '.......', '...kkk.', '..kkggg', '.wkggg.', 'wkw.g..', 'kw.y...', '..y.y..'] };
const HERON_K = { k: '#161a24', g: '#7a7e8c', w: '#d8d8d4', y: '#d0b030' };

/* ================= The cast ================= */
function drawCouple(t) {
  const [he, she] = SPOTS.couple, fy = BENCH.y - 6;
  const rest = inWin(t, CONCERT.peak, CONCERT.fall), point = inWin(t, PRUSHOW.t0 + 10, PRUSHOW.t0 + 18), lean = !rest && frac(t / 40 + 0.2) < 0.2;
  person(he, fy, L_HE, { view: 0, sit: true, aR: rest ? 5 : 0, lean: lean ? 1 : 0 });
  person(she, fy, L_SHE, { view: 0, sit: true, aR: point ? 4 : 0, lean: rest ? -2 : lean ? -1 : 0 });   // her head on his shoulder at the concert
}
const handoff = t => frac((t - 9) / 24);                                                   // a roll goes over the counter at 9, 33, 57 ...
function drawVendor(t) {
  const h = handoff(t), give = h < 0.06 || h > 0.97, x = give ? SHACK.cx : SHACK.cx - 6 + Math.round(10 * (0.5 + 0.5 * Math.sin(TAU * frac(t / 8)))), top = WIN.y0 + 5;
  R(x - 3, top + 5, 7, WIN.y1 - top - 4, VENDOR.top); R(x - 3, top + 5, 7, 1, mixHex(VENDOR.top, '#ffe0a0', 0.4));
  R(x - 2, top, 4, 5, VENDOR.skin); R(x - 2, top, 4, 2, VENDOR.hair); R(x - 2, top - 1, 4, 2, VENDOR.hat);
  if (give) { R(x + 3, top + 7, 3, 1, VENDOR.skin); R(x + 5, top + 6, 3, 2, '#f0d090'); dot(x + 6, top + 6, '#e8604a'); }   // a lobster roll
  for (let k = 0; k < 3; k++) {                                                             // the fryer's steam
    const { u } = cycle(t, 240, k / 3);
    ctx.globalAlpha = 0.3 * (1 - u); dot(WIN.x1 - 6 + wave(t, 36, k * 0.4), WIN.y0 + 7 - u * 8, '#fff4e0'); ctx.globalAlpha = 1;
  }
}
function drawQueue(t, crowd) {
  [[0, RANK.queue], [24, RANK.queue2]].forEach(([off, rank], slot) => {
    if (!here(rank, crowd)) return;
    const q = t - off + 480, u = q % 48, c = (Math.floor(q / 48) + slot * 2) % CUSTOMERS.length, P = CUSTOMERS[c];
    if (u < 10) { const x = lerp(500, 302, u / 10); person(x, 220, P, { view: 2, dir: -1, step: Math.floor(x / 4) & 1 }); }
    else if (u < 24) person(302, 220, P, { view: u < 18 ? 0 : 2, dir: -1, aR: u > 13 && u < 20 ? 2 : 0 });
    else if (u < 34) person(283, 219, P, { view: 0, aR: u > 32 ? 3 : 0, aL: u > 27 && u < 30 ? 2 : 0 });
    else { const x = lerp(283, -20, (u - 34) / 14); person(x, LANE_A, P, { view: 2, dir: -1, step: Math.floor(x / 4) & 1, aR: frac(u / 5) < 0.3 ? 2 : 3 }); }
  });
}
function drawFisher(t) {
  const x = SPOTS.fisher, fy = EDGE, u = frac(t / 48 + 0.15), ty = tyOf(L_FISH, fy);
  const cast = u < 0.08, reel = u > 0.7 && u < 0.85, c = cast ? Math.sin(Math.PI * u / 0.08) : 0;
  person(x, fy, L_FISH, { view: 0, aL: 3 });
  const hx = x - 5, hy = ty + 2, tipX = Math.round(x - 16 + c * 20), tipY = Math.round(ty - 14 - c * 4);
  const n = Math.max(Math.abs(tipX - hx), Math.abs(tipY - hy));
  for (let k = 0; k <= n; k++) dot(lerp(hx, tipX, k / n), lerp(hy, tipY, k / n), k < n * 0.3 ? '#3a2e24' : '#8a7c62');
  const lx = x - 24, ly = QY - 9, m = Math.max(1, Math.abs(ly - tipY));
  ctx.globalAlpha = 0.45; ctx.fillStyle = '#d8dce8';
  for (let k = 1; k <= m; k++) ctx.fillRect(Math.round(lerp(tipX, lx, k / m) + Math.sin(Math.PI * k / m) * (reel ? 0.5 : 2)), Math.round(lerp(tipY, ly, k / m)), 1, 1);
  ctx.globalAlpha = 1;
  dot(lx, ly, '#ff5a3a'); dot(lx, ly + 1, '#e8e4dc');
  if (u > 0.08 && u < 0.14) { ctx.globalAlpha = 0.5 * (1 - (u - 0.08) / 0.06); const rr = 1 + (u - 0.08) * 60; for (let j = 0; j < 8; j++) { const a = j / 8 * TAU; dot(lx + Math.cos(a) * rr, ly + 1 + Math.sin(a) * rr * 0.3, '#c8d4ec'); } ctx.globalAlpha = 1; }
  if (reel) dot(hx + 1, hy + 2, '#8a8c96');
}
function drawFriends(t) {
  const [a, b] = SPOTS.friends, fy = QY + 1, eat = frac(t / 20 + 0.1);
  const point = inWin(t, DUCK.t0 + 6, DUCK.t0 + 18) || inWin(t, FLEET.t0 + 20, FLEET.t0 + 30);
  person(a, fy, L_FRA, { view: 0, sit: true, aR: eat < 0.15 ? 2 : 0, lean: frac(t / 30) < 0.12 ? 1 : 0 });
  if (eat < 0.15) dot(a + 2, tyOf({ ll: 0, th: L_FRA.th }, fy) - 1, '#f0d090');             // a lobster roll on its way
  person(b, fy, L_FRB, { view: 0, sit: true, aR: point ? 4 : 0, lean: frac(t / 30 + 0.5) < 0.1 ? -1 : 0 });
  R(a + 4, QY - 2, 4, 2, '#e8e0d0'); dot(a + 5, QY - 2, '#c82a1e');                          // the shack's paper boat between them
}
function drawKid(t) {
  const fy = EDGE, x = SPOTS.kid, wv = inWin(t, WAVE[0], WAVE[1]), s = step(t, 480);
  const train = TRAINS.some(T => inWin(t, T.t0 + 4, T.t0 + 14));
  person(SPOTS.parent, fy, L_PARENT, { view: 0, aR: train || wv ? 3 : 0, lean: wv ? 1 : 0 });
  if (wv) person(x, fy, L_KID, { view: 0, aL: 1, aR: s % 4 < 2 ? 1 : 3 });                  // waving at the Duck Boat
  else if (train) person(x, fy, L_KID, { view: 0, aL: 4 });                                  // pointing at the train on the bridge
  else { const h = hash(step(t, 60), 7); person(x, fy, L_KID, { view: h < 0.2 ? 1 : 0, aR: h > 0.8 ? 3 : 0, lean: h > 0.5 && h < 0.6 ? 1 : 0 }); }
}
function drawPhone(t) {
  const x = SPOTS.phone, fy = EDGE, film = inWin(t, PRUSHOW.t0 + 4, PRUSHOW.t1 - 6) || inWin(t, DUCK.t0 + 10, DUCK.t0 + 34), ty = tyOf(L_PHONE, fy);
  if (film) { person(x, fy, L_PHONE, { view: 0, aL: 2, aR: 2 }); R(x - 1, ty - 4, 3, 2, '#9ad0ff'); halo(HL.screen, x, ty - 3, 0.8); }
  else { person(x, fy, L_PHONE, { view: 0, aR: 2 }); dot(x + 3, ty + 1, '#9ad0ff'); }
}
function drawSailor(t) {
  const fy = DOCK.y, arrive = FLEET.dock;
  let x = 414, o;
  if (inWin(t, arrive - 6, arrive + 12)) { x = 406; o = { view: 0, aL: 3, aR: 3 }; }              // catching the boats coming in
  else if (inWin(t, arrive + 12, FLEET.t1)) { x = lerp(406, 470, (t - arrive - 12) / (FLEET.t1 - arrive - 12)); o = { view: 2, dir: 1, step: Math.floor(x / 3) & 1, aR: 3 }; }
  else if (t >= FLEET.t1) { x = lerp(470, 414, (t - FLEET.t1) / (LOOP - FLEET.t1)); o = { view: 2, dir: -1, step: Math.floor(x / 3) & 1 }; }
  else { const c = frac(t / 2.4); o = { view: 0, aL: c < 0.5 ? 2 : 3, aR: c < 0.5 ? 3 : 2 }; }   // coiling a line, arm over arm
  person(x, fy, L_SAILOR, o);
}
function drawDogWalker(t) {
  const { t0, t1, stop } = DOGW;
  if (t < t0 || t >= t1) return;
  const s = along([[t0, 500], [stop[0], 190], [stop[1], 190], [t1, -40]], t), x = Math.round(s.x), dx = x - 16, sniff = inWin(t, stop[0], stop[1]);
  const df = sniff ? 2 : s.moving ? (Math.floor(x / 3) & 1) : 0;
  ctx.fillStyle = 'rgba(8,10,24,0.3)'; ctx.fillRect(dx - 4, LANE_A + 1, 9, 1);
  ctx.drawImage(DOG_SPR[df][1], dx - 6, LANE_A - 6);
  person(x, LANE_A, L_DOGW, { view: sniff ? 0 : 2, dir: -1, step: s.moving && (Math.floor(x / 4) & 1), aR: 3, aL: sniff ? 3 : 0 });
  const hx = x - 4, hy = tyOf(L_DOGW, LANE_A) + 2, cx = dx - 2, cy = LANE_A - 4, n = Math.abs(cx - hx);
  ctx.fillStyle = '#c8323a';
  for (let k = 1; k < n; k++) ctx.fillRect(hx + Math.sign(cx - hx) * k, Math.round(lerp(hy, cy, k / n) + Math.sin(Math.PI * k / n) * 2), 1, 1);
}
function drawBikes(t, crowd) {
  BIKES.forEach((B, bi) => {
    if (t < B.t0 || t >= B.t1 || !here(bi ? RANK.bike2 : RANK.bike1, crowd)) return;
    const u = (t - B.t0) / (B.t1 - B.t0), x = B.dir > 0 ? lerp(-30, W + 30, u) : lerp(W + 30, -30, u);
    for (let i = 0; i < B.n; i++) drawBike(Math.round(x - B.dir * i * 22), LANE_B - i * 3, B.dir, L_BIKE[bi + i], t);
  });
}
function drawJogger(t) {
  JOG.forEach((J, i) => {
    if (t < J.t0 || t >= J.t1) return;
    const u = (t - J.t0) / (J.t1 - J.t0), d = J.dir, x = Math.round(d > 0 ? lerp(-20, W + 20, u) : lerp(W + 20, -20, u)), f = Math.floor(x / 5) & 1;
    person(x, LANE_B - f, i ? L_JOG2 : L_JOG, { view: 2, dir: d, step: f, aR: 4 });
  });
}
function drawStroll(t) {
  if (t < STROLL.t0 || t >= STROLL.t1) return;
  const x = Math.round(lerp(-30, W + 24, (t - STROLL.t0) / (STROLL.t1 - STROLL.t0))), f = Math.floor(x / 4) & 1;
  person(x - 8, LANE_A + 1, L_STROLL_B, { view: 2, dir: 1, step: !f });
  person(x, LANE_A, L_STROLL_A, { view: 2, dir: 1, step: f, aR: 3 });
}
function drawMallards(t) {
  MALLARDS.forEach((m, i) => {
    const a = TAU * (m.k * t / LOOP + m.ph), x = Math.round(m.x + m.a * Math.sin(a)), y = m.y + Math.round(0.5 * wave(t, 30, i * 0.3));
    const d = Math.cos(a) > 0 ? 0 : 1;
    ctx.globalAlpha = 0.25; R(x - 4, y + 1, 9, 1, '#8890b8'); ctx.globalAlpha = 1;
    ctx.drawImage(DUCK_SPR[m.drake ? 0 : 1][d], x - 4, y - 4);
    if (frac(t / 3 + i * 0.4) < 0.5) { ctx.globalAlpha = 0.3; dot(x + (d ? 5 : -5), y + 1, '#c8d0e8'); ctx.globalAlpha = 1; }   // their little wakes
  });
}
function drawHeron(t) {
  const strike = HERON.some(s => inWin(t, s, s + 1.6)), x = DOCK.x0 - 1, y = DOCK.y - 15;
  ctx.drawImage(strike ? HERON_SPR.strike : HERON_SPR.stand, x, y);
  if (HERON.some(s => inWin(t, s + 0.6, s + 2.4))) {                                         // the ring where it struck
    const s0 = HERON.find(s => inWin(t, s + 0.6, s + 2.4)), rr = 1 + (t - s0 - 0.6) * 3;
    ctx.globalAlpha = 0.4 * (1 - (t - s0 - 0.6) / 1.8);
    for (let j = 0; j < 8; j++) { const a = j / 8 * TAU; dot(x - 1 + Math.cos(a) * rr, DOCK.y + 1 + Math.sin(a) * rr * 0.3, '#c8d4ec'); }
    ctx.globalAlpha = 1;
  }
}

/* ================= Prerendered state ================= */
let HL, SKY_C, WISPS, FAR_C, WEST_C, BB_C, HERO_C, HILL_C, BANK_C, CAMB_C, LF, WATER_C, MIRROR_C, SHACK_C, BOARD_C, AD_C, PITCH,
  GROUND_C, LAMP_C, BENCH_C, SYCL_C, SYCR_C, DOCK_C, DUCK_C, DOG_SPR, DUCK_SPR, HERON_SPR, VIG_C, PRU_TINT;
const SPLIT = {};
const TOP_LAMPS = [];
function renderVignette() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.74, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H * 0.48) / (H / 2) * 0.9)), q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, [5, 7, 20], Math.round(q * 0.4 * 255));
  }
  return p.done();
}
function prerender() {
  for (const L of [WINS, TOP_LAMPS, ESP_LAMPS, CB_MASTS, LF_LAMPS, SYC_TIPS]) L.length = 0;
  HL = {
    city: makeHalo(80, '#ffb878', 0.05, 1.5), pru: makeHalo(18, '#e8f0ff', 0.2, 1.4), hun: makeHalo(12, '#d8e8ff', 0.24, 1.4),
    dome: makeHalo(9, '#ffd060', 0.3, 1.3), beacon: makeHalo(7, '#4a7cff', 0.5, 1.2), beaconR: makeHalo(7, '#ff3a30', 0.5, 1.2),
    red: makeHalo(3, '#ff3a30', 0.5, 1), tiny: makeHalo(3, '#fff4d0', 0.5, 1.1), lamp: makeHalo(42, '#ffc878', 0.16, 1.7),
    lampCore: makeHalo(9, '#fff2d0', 0.36, 1.3), elamp: makeHalo(3, '#ffd890', 0.4, 1.2), head: makeHalo(5, '#f4f6ff', 0.4, 1.3),
    land: makeHalo(8, '#f4f6ff', 0.4, 1.3), screen: makeHalo(4, '#9ad0ff', 0.4, 1.2), shack: makeHalo(30, '#ffb860', 0.16, 1.5),
    sign: makeHalo(22, '#ffe2b0', 0.14, 1.5), concert: makeHalo(46, '#ffc070', 0.42, 1.4), concertCore: makeHalo(13, '#fff0c8', 0.5, 1.3),
    pruR: makeHalo(18, '#ff4050', 0.24, 1.4), pruB: makeHalo(18, '#4070ff', 0.24, 1.4), han: makeHalo(30, '#e8a090', 0.06, 1.5),
  };
  SKY_C = renderSky();
  WISPS = [makeWisps(11, 76, 8, '#28305e', '#6a5478', 92), makeWisps(29, 60, 7, '#323868', '#866078', 80), makeWisps(47, 54, 6, '#3a4070', '#8a6a82', 66)];
  const r = rng(1923);
  const layer = (fn) => { const p = pix(W, WL + 8); fn(p); return trim(p); };
  SPLIT.far = WINS.length;
  FAR_C = layer(p => { for (const b of DOWNTOWN) paintTower(p, r, b); });
  for (const b of DOWNTOWN) if (b.beaconAt) TOP_LAMPS.push(b.beaconAt);
  SPLIT.west = WINS.length;
  WEST_C = layer(p => { for (const b of WESTEND) paintTower(p, r, b); });
  BB_C = layer(p => { for (const b of BACKBAY) paintTower(p, r, b); });
  SPLIT.hero = WINS.length;
  HERO_C = layer(p => { paintBerkeley(p, r); paintHancock(p, r); paintHuntington(p, r); paintPru(p, r); paintDalton(p, r); });
  SPLIT.bank = WINS.length;
  HILL_C = layer(p => paintHill(p, r));
  BANK_C = layer(p => paintBank(p, r));
  CAMB_C = (() => { const p = pix(W, H); paintCambridge(p, r); return trim(p); })();
  SPLIT.end = WINS.length;
  LF = renderLongfellow();
  PRU_TINT = {};
  for (const [k, col] of Object.entries({ red: '#e8303a', blue: '#3a6ae8', white: '#f4f8ff' })) {
    const [c, x] = makeCanvas(PRU.x1 - PRU.x0, 7); x.fillStyle = col; x.fillRect(0, 0, PRU.x1 - PRU.x0, 7);
    x.fillStyle = mixHex(col, '#000000', 0.35); for (let i = 0; i < PRU.x1 - PRU.x0; i += 2) x.fillRect(i, 0, 1, 7);
    PRU_TINT[k] = c;
  }
  WATER_C = renderWater();
  {
    const [c, x] = makeCanvas(W, WL);
    x.drawImage(SKY_C, 0, 0);
    for (const L of [FAR_C, WEST_C, BB_C, HERO_C, HILL_C, BANK_C, LF.back, LF.front, CAMB_C]) x.drawImage(L.c, L.x, L.y);
    MIRROR_C = renderMirror(c);
  }
  SHACK_C = renderShack(); BOARD_C = renderBoard(); AD_C = renderAd();
  PITCH = makePitch(BB.w, BB.h, { accent: ACCENT });
  GROUND_C = renderGround(); LAMP_C = renderLamp(); BENCH_C = renderBench(); DOCK_C = renderDock();
  SYCL_C = renderSycamore(-1); SYCR_C = renderSycamore(1);
  DUCK_C = sprite(DUCK_ROWS, DUCK_KEY);
  DOG_SPR = DOG_ROWS.map(rows => [sprite(rows, DOG_KEY), sprite(rows, DOG_KEY, true)]);
  DUCK_SPR = [[sprite(DRAKE, DUCK_K, true), sprite(DRAKE, DUCK_K)], [sprite(HEN, DUCK_K, true), sprite(HEN, DUCK_K)]];
  const two = rows => [sprite(rows, GOOSE_KEY), sprite(rows, GOOSE_KEY, true)];
  GOOSE_SPR = { walk: GOOSE_WALK.map(two), graze: two(GOOSE_GRAZE) };
  HERON_SPR = { stand: sprite(HERON_ROWS.stand, HERON_K), strike: sprite(HERON_ROWS.strike, HERON_K) };
  VIG_C = renderVignette();
}

/* ================= Per-frame: the sky, the city ================= */
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.4 + 0.6 * Math.max(0, wave(t, s.k, s.ph))); ctx.fillStyle = s.c; ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  WISPS.forEach((c, i) => { const off = scroll(t, i === 2 ? 1 : i + 1, W), y = [-4, 34, 78][i]; ctx.drawImage(c, -off, y); ctx.drawImage(c, W - off, y); });
  drawJets(t);
  drawApproach(t);
}
const winLit = (w, t) => frac(w.k * t / LOOP + w.ph) < w.duty;
function drawWins(a, b, t) {
  for (let i = a; i < b; i++) { const w = WINS[i]; if (winLit(w, t)) R(w.x, w.y, w.w, w.h, w.c); }
}
/* the Prudential's crown: plain white most of the loop; the show turns it Red Sox red, then blue, then white again */
function pruShow(t) {
  const { t0, t1 } = PRUSHOW;
  if (t < t0 || t >= t1) return null;
  const s = t - t0;
  if (s < 6) return ['white', 'red', smooth(0, 6, s)];
  if (s < 24) return ['red', 'red', 0];
  if (s < 30) return ['red', 'blue', smooth(24, 30, s)];
  if (s < 48) return ['blue', 'blue', 0];
  return ['blue', 'white', smooth(48, 56, s)];
}
const SHOW_COL = { white: '#e8eeff', red: '#ff5a64', blue: '#6a90ff' };
let pruTint = null;
function drawPruCrown(t) {
  const sh = pruShow(t), y = PRU.top + 2;
  pruTint = null;
  if (sh) {
    const [a, b, m] = sh;
    for (const [k, al] of [[a, 1 - m], [b, m]]) if (k !== 'white' && al > 0.01) { ctx.globalAlpha = al * 0.88; ctx.drawImage(PRU_TINT[k], PRU.x0, y); }
    ctx.globalAlpha = 1;
    pruTint = m < 0.5 ? a : b;
    if (pruTint !== 'white') halo(pruTint === 'red' ? HL.pruR : HL.pruB, PRU.x, y + 3, 0.9);
  }
  halo(HL.pru, PRU.x, y + 3, sh && pruTint !== 'white' ? 0.4 : 0.8);
  const b = beacon(t);                                                                       // the mast's aircraft lamps
  for (const yy of [PRU.mast, PRU.mast + 9]) { ctx.globalAlpha = 0.35 + 0.65 * b; dot(PRU.x, yy, '#ff3a30'); ctx.globalAlpha = 1; halo(HL.red, PRU.x, yy, b); }
  for (const xx of [HAN.x0 + 1, HAN.x1 - 2]) { ctx.globalAlpha = 0.35 + 0.65 * b; dot(xx, HAN.top, '#ff3a30'); ctx.globalAlpha = 1; halo(HL.red, xx, HAN.top, b * 0.8); }
}
/* the Berkeley beacon: steady blue, clear view; flashing blue, clouds due soon; steady red, rain ahead; flashing red,
   snow instead. It forecasts the weather on show: the city's own is clear */
function drawBeacon(t, weather) {
  const wet = weather === 'drizzle' || weather === 'rain' || weather === 'thunderstorm', snow = weather === 'snow';
  const red = wet || snow, on = !snow || frac(t / 2) < 0.6, col = red ? '#ff4a3a' : '#5a8cff', top = BER.top;
  if (!on) { R(BER.x - 1, top, 2, 3, red ? '#4a1a1a' : '#1a2a5a'); return; }
  R(BER.x - 1, top + 1, 2, 2, col); dot(BER.x, top, red ? '#ffb0a0' : '#c8dcff'); dot(BER.x - 1, top + 3, mixHex(col, '#000000', 0.4));
  halo(red ? HL.beaconR : HL.beacon, BER.x, top + 1, 0.9);
}
/* the concert at the Hatch Shell: its stage opens away from us, so what Cambridge sees is the glow over its back */
const concert = t => 0.22 + 0.78 * smooth(CONCERT.t0, CONCERT.peak, t) * (1 - smooth(CONCERT.fall, CONCERT.t1, t));
function drawConcert(t) {
  const c = concert(t);
  halo(HL.concert, HATCH.x + 7, WL - 3, c);
  halo(HL.concertCore, HATCH.x + 6, WL - 2, c * 0.7);
  ctx.globalAlpha = 0.25 + 0.6 * c; ctx.fillStyle = '#ffd890';                               // its rim catching the stage lights
  for (let i = 1; i <= HATCH.w / 2; i++) { const u = i / (HATCH.w / 2), yt = Math.round(WL - 1 - (WL - 1 - HATCH.top) * Math.sqrt(Math.max(0, 1 - u * u))); ctx.fillRect(HATCH.x + i, yt, 1, 1); }
  ctx.globalAlpha = 1;
}
const STORROW = [[1, 7, 0.1], [-1, 6, 0.3], [1, 9, 0.55], [-1, 8, 0.75], [1, 5, 0.9], [-1, 10, 0.45]];
function drawFar(t, weather) {
  halo(HL.city, 230, 158, 1); halo(HL.city, 370, 158, 0.9);
  blit(FAR_C); drawWins(SPLIT.far, SPLIT.west, t);
  const b = beacon(t);
  for (const [x, y] of TOP_LAMPS) { ctx.globalAlpha = 0.35 + 0.65 * b; dot(x, y, '#ff3a30'); ctx.globalAlpha = 1; halo(HL.red, x, y, b * 0.7); }
  blit(WEST_C); blit(BB_C); drawWins(SPLIT.west, SPLIT.hero, t);
  halo(HL.han, HAN.x, 112, 1);
  blit(HERO_C); drawWins(SPLIT.hero, SPLIT.bank, t);
  for (let k = 0; k < 2; k++) {                                                              // the drifting cloud, mirrored in its glass
    const u = frac(t / 60 + k * 0.5), yy = Math.round(HAN.top + 2 + u * (WL - HAN.top - 8));
    ctx.globalAlpha = 0.16 * Math.sin(Math.PI * u); ctx.fillStyle = '#ffdcd0';
    ctx.fillRect(HAN.x0 + 1, yy, 5, 4); ctx.fillRect(HAN.x0 + 7, yy + 1, 5, 4);
    ctx.globalAlpha = 1;
  }
  drawPruCrown(t); drawBeacon(t, weather);
  halo(HL.hun, HUN.x, HUN.top + 4, 0.85 + 0.15 * wave(t, 10));
  blit(HILL_C); halo(HL.dome, SH.x, SH.top + 4, 0.85 + 0.15 * wave(t, 8));
  blit(BANK_C); drawWins(SPLIT.bank, SPLIT.end, t);
  drawConcert(t);
  for (const [d, k, ph] of STORROW) {                                                        // cars on Storrow Drive, glimpsed between the trees
    const x = Math.round(lerp(d > 0 ? 170 : W + 4, d > 0 ? W + 4 : 170, frac(k * t / LOOP + ph)));
    if (!STORROW_GAP[x >> 2]) continue;
    const y = d > 0 ? WL - 3 : WL - 2;
    dot(x, y, '#fff2d8'); dot(x - d * 2, y, '#ff4a3a');
  }
  for (const x of ESP_LAMPS) { dot(x, WL - 3, '#ffe2a8'); halo(HL.elamp, x, WL - 3, 0.8); }
  for (const mx of CB_MASTS) if (hash(mx, 3) < 0.5) dot(mx, WL - 6, '#fff4e0');
  blit(LF.back); drawTrain(t); blit(LF.front);
  for (const [x, y] of LF_LAMPS) { dot(x, y, '#ffe4b0'); halo(HL.elamp, x, y, 0.7); }
  blit(CAMB_C);
  drawMemDrive(t);
  for (const [x, y, n] of CAMB_LAMPS) { R(x, y - n, 1, n, '#0e1016'); dot(x, y - n, '#fff0c8'); halo(n > 5 ? HL.lampCore : HL.elamp, x, y - n, 0.8); }
}

/* ================= Per-frame: the water, and what floats on it ================= */
function drawWater(t) {
  ctx.drawImage(WATER_C, 0, WL);
  const rows = QY - WL;
  for (let k = 0; k < rows; k++) {
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + k * 0.55) * (0.6 + k * 0.07) + Math.sin(TAU * 110 * t / LOOP - k * 1.3) * 0.6);
    ctx.globalAlpha = 0.9 * (1 - k / (rows + 16)) * (0.84 + 0.16 * Math.sin(TAU * 60 * t / LOOP + k * 1.7));
    ctx.drawImage(MIRROR_C, 0, k, W, 1, off, WL + k, W, 1);
  }
  for (const g of GLINTS) {
    const v = wave(t, g.kf, g.ph);
    if (v < -0.1) continue;
    ctx.globalAlpha = 0.5 * Math.min(1, v + 0.1);
    ctx.fillStyle = g.c; ctx.fillRect(Math.round(frac(g.x + g.k * t / LOOP) * (W + 30)) - 15, g.y, g.len, 1);
  }
  ctx.globalAlpha = 1;
  for (const s of STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, WL + 1, QY);
  streak(ctx, t, PRU.x, 6, pruTint ? SHOW_COL[pruTint] : '#e8eeff', pruTint && pruTint !== 'white' ? 0.3 : 0.2, 1, WL + 1, QY);   // the Pru's long reflection
  streak(ctx, t, HATCH.x + 4, 7, '#ffc878', 0.06 + 0.2 * concert(t), 21, WL + 1, QY);
  for (const L of LAMPS) streak(ctx, t, L.x, 3, '#ffd890', 0.22, L.x, QY - 12, QY);
  streak(ctx, t, SHACK.cx, 9, '#ffc070', 0.16, 33, QY - 10, QY);
}
function drawBoats(t, crowd) {
  drawBats(t);
  drawBeaters(t);
  drawDuck(t, crowd);
  if (here(RANK.eight, crowd)) drawEight(t);
  if (here(RANK.scull, crowd)) drawScull(t);
  drawMoored(t);
  drawFleet(t);
  drawTied(t);
  blit(DOCK_C);
  dot(454, DOCK.y - 10, '#fff0c8'); halo(HL.tiny, 454, DOCK.y - 10, 0.8);                    // the dock light
  drawHeron(t);
  if (here(RANK.sailor, crowd)) drawSailor(t);
  drawMallards(t);
}

/* ================= Per-frame: the Cambridge side ================= */
const BB_FLICK = [[47.2, 48.4], [171.6, 172.3]];    // one of the sign's lamps stutters now and then
function drawBoard(t, pitch) {
  const fl = BB_FLICK.some(([a, b]) => t >= a && t < b) && hash(step(t, 2400), 13) < 0.5;
  halo(HL.sign, BB.x + 23, BB.y + 9, fl ? 0.4 : 0.7);
  blit(BOARD_C);
  if (!PITCH.covers(t, pitch)) {
    ctx.drawImage(AD_C, BB.x, BB.y);
    if (fl) { ctx.globalAlpha = 0.3; R(BB.x + BB.w / 2, BB.y, BB.w / 2, BB.h, '#0a0c18'); ctx.globalAlpha = 1; }
  }
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  for (const [lx, on] of [[BB.x + 13, true], [BB.x + BB.w - 8, !fl]]) { dot(lx, BB.y - 6, on ? '#fff6dc' : '#6a6050'); if (on) halo(HL.tiny, lx, BB.y - 5, 0.9); }
}
function drawPromenade(t, crowd, pitch, billboard) {
  const DL = [], put = (fy, fn) => DL.push([fy, fn]);
  put(SHACK.base, () => {
    halo(HL.shack, SHACK.cx, 181, 0.7);
    blit(SHACK_C);
    if (here(RANK.vendor, crowd)) drawVendor(t);
    if (billboard) drawBoard(t, pitch);
  });
  for (const L of LAMPS) put(EDGE + 4, () => ctx.drawImage(LAMP_C, L.x - 4, L.y - 6));
  put(BENCH.y, () => { if (here(RANK.couple, crowd)) drawCouple(t); ctx.drawImage(BENCH_C, BENCH.x, BENCH.y - 13); });
  if (here(RANK.kid, crowd)) put(EDGE, () => drawKid(t));
  if (here(RANK.friends, crowd)) put(QY + 1, () => drawFriends(t));
  if (here(RANK.fisher, crowd)) put(EDGE + 0.2, () => drawFisher(t));
  if (here(RANK.phone, crowd)) put(EDGE + 0.4, () => drawPhone(t));
  put(219.5, () => drawQueue(t, crowd));
  if (here(RANK.dog, crowd)) put(LANE_A, () => drawDogWalker(t));
  if (here(RANK.stroll, crowd)) put(LANE_A + 0.5, () => drawStroll(t));
  put(LANE_B, () => drawBikes(t, crowd));
  if (here(RANK.jog, crowd)) put(LANE_B + 0.5, () => drawJogger(t));
  LAWN_GEESE.forEach((g, i) => put(g.y, () => drawGoose(g, i, t)));
  put(SYC_L.base, () => blit(SYCL_C));
  put(SYC_R.base, () => blit(SYCR_C));
  DL.sort((a, b) => a[0] - b[0]);
  for (const [, fn] of DL) fn();
  for (const m of SYC_TIPS) if (wave(t, m.k, m.ph) > 0.72) dot(m.x, m.y, m.col);              // leaves stirring at the crowns' edges
  for (const L of LAMPS) { const I = 0.94 + 0.04 * wave(t, 900, L.x * 0.01); halo(HL.lamp, L.x, L.y + 2, I); halo(HL.lampCore, L.x, L.y, I); }
  drawMoths(t);
}
/* moths round the riverside lamps on a warm night */
function drawMoths(t) {
  LAMPS.forEach((L, li) => {
    for (let k = 0; k < 3; k++) {
      const a = TAU * ((36 + k * 12 + li * 6) * t / LOOP + k / 3 + li * 0.2), r = 4 + k * 2 + Math.sin(TAU * (7 * t / LOOP + k)) * 1.5;
      const x = L.x + r * Math.cos(a) + (hash(k, li, step(t, 4800)) - 0.5), y = L.y - 1 + r * 0.7 * Math.sin(a * 1.3);
      ctx.globalAlpha = 0.75; dot(x, y, '#f0e6cc'); ctx.globalAlpha = 1;
    }
  });
}

/* the breeze off the river running through the lawn's grass, in slow waves */
function drawGrass(t) {
  for (let y = GRASS + 3; y < H; y += 2) for (let x = (y * 3) % 2; x < W; x += 2) {
    const v = Math.sin(TAU * (x / 96 - 5 * t / LOOP) + y * 0.11) + 0.4 * Math.sin(TAU * (x / 37 + 7 * t / LOOP) - y * 0.2);
    if (v > 0.9 && hash(x, y, 61) < 0.6) { ctx.fillStyle = v > 1.2 ? '#2e4228' : '#22321e'; ctx.fillRect(x, y, 1, 1); }
  }
}
/* Canada geese grazing on the lawn, as they do all along the Charles */
const GOOSE_KEY = { k: '#16161a', w: '#f0ece4', b: '#6e5c46', B: '#4a3e32', p: '#bfb2a0', t: '#1e1e22', y: '#3a3430' };
const GOOSE_WALK = [
  ['...kk...........', '..kkk...........', '..kwk...........', '...wk...........', '...k............', '...k.......BB...', '...kk...bbbbBBB.', '....pbbbbbbbbBBt',
    '....ppbbbbbbBBtt', '.....pppppbbBt..', '.......pppp.....', '.......y..y.....', '......yy.yy.....'],
  ['...kk...........', '..kkk...........', '..kwk...........', '...wk...........', '...k............', '...k.......BB...', '...kk...bbbbBBB.', '....pbbbbbbbbBBt',
    '....ppbbbbbbBBtt', '.....pppppbbBt..', '.......pppp.....', '........yy......', '.......yyy......']];
const GOOSE_GRAZE = ['................', '................', '................', '................', '................', '...........BB...', '........bbbbBBB.',
  '....bbbbbbbbbBBt', '...kpbbbbbbbBBtt', '..kk.pppppbbBt..', '.kk....pppp.....', 'kwk....y..y.....', 'kk....yy.yy.....'];
let GOOSE_SPR;
const LAWN_GEESE = [{ x: 372, y: 252, r: 9, k: 2, ph: 0.1 }, { x: 398, y: 259, r: 12, k: 1, ph: 0.4 }, { x: 426, y: 248, r: 7, k: 3, ph: 0.7 }];
function drawGoose(g, i, t) {
  const a = TAU * (g.k * t / LOOP + g.ph), x = g.x + g.r * Math.sin(a), dir = Math.cos(a) > 0 ? 1 : 0;
  const graze = hash(step(t, 120), i, 51) < 0.6;
  ctx.fillStyle = 'rgba(8,10,24,0.32)'; ctx.fillRect(Math.round(x) - 7, g.y + 1, 14, 1);
  ctx.drawImage(graze ? GOOSE_SPR.graze[dir] : GOOSE_SPR.walk[(step(t, 480) + i) & 1][dir], Math.round(x) - 8, g.y - 12);
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures) on the shack's sign; billboard: false leaves the sign out (the loop
// videos); weather: a WEATHERS pick (site/js/engine/weather.js), the city's own (clear) when left out; crowd: the share
// of the people to show, 0..1 (CROWD when left out or not a number); 0 = nobody at all
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  drawSky(t);
  drawFar(t, weather);
  drawWater(t);
  drawBoats(t, crowd);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  blit(GROUND_C);
  drawGrass(t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawPromenade(t, crowd, pitch, billboard);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'boston',
  name: 'Boston',
  tagline: 'Summer sails on the Charles',
  timeZone: 'America/New_York',
  accent: '#44bb11',                                   // the Green Line's green, written out: tools/seo/seo.mjs reads it from the source
  focusX: 366,
  landmark: [HAN.x0 - 1, DAL.x1],                      // the Back Bay skyline: 200 Clarendon, 111 Huntington, the Pru, One Dalton
  landmarkTop: PRU.mast,                               // the Prudential's mast
  billboard: BB,
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  spots: [
    { kind: 'landmark', x: PRU.x0, y: PRU.mast, w: PRU.x1 - PRU.x0, h: WL - PRU.mast },
    { kind: 'landmark', x: HAN.x0, y: HAN.top, w: HAN.x1 - HAN.x0, h: WL - HAN.top },
    { kind: 'window', x: WIN.x0, y: WIN.y0, w: WIN.x1 - WIN.x0 + 1, h: 10 },
    { kind: 'water', x: 140, y: 174, w: 70, h: 11 },
  ],
  ambience: { water: 0.32, wind: 0.12, traffic: 0.16, crickets: 0.28, crowd: 0.04 },
  events: [
    { t: TRAINS[0].t0 + 1, type: 'train', x: 60, gain: 0.2 },
    { t: DUCK.t0 + 1, type: 'boat', dur: 62, pan: [0.95, -0.55], gain: 0.22 },
    { t: BIKES[0].t0 + 1, type: 'chime', x: 420, gain: 0.1 },
    { t: HERON[0] + 0.7, type: 'splash', x: DOCK.x0, gain: 0.1 },
    { t: TRAINS[1].t0 + 1, type: 'train', x: 170, gain: 0.2 },
    { t: HERON[1] + 0.7, type: 'splash', x: DOCK.x0, gain: 0.1 },
    { t: CONCERT.peak + 1, type: 'chime', x: HATCH.x, gain: 0.16 },
    { t: TRAINS[2].t0 + 1, type: 'train', x: 60, gain: 0.2 },
    { t: CONCERT.fall, type: 'chime', x: HATCH.x, gain: 0.1 },
    { t: BIKES[1].t0 + 1, type: 'chime', x: 60, gain: 0.1 },
    { t: TRAINS[3].t0 + 1, type: 'train', x: 170, gain: 0.2 },
    { t: HERON[2] + 0.7, type: 'splash', x: DOCK.x0, gain: 0.1 },
    { t: FLEET.dock + 0.5, type: 'splash', x: 398, gain: 0.14 },
    { t: FLEET.dock + 3, type: 'splash', x: 412, gain: 0.12 },
    { t: FLEET.dock + 5.5, type: 'splash', x: 426, gain: 0.12 },
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
